// Ultimate Card Game (UCG) - Battlefield Background Rendering
//
// Extracted from js/engine/renderer.js (was its single largest method, at
// ~3340 lines - lane terrain, parallax skyline, weather-reactive lighting,
// starfield, and everything else painted behind bases/units/particles).
// Mixed onto Renderer.prototype (see the Object.assign() call at the
// bottom of renderer.js) rather than kept as a free function, since it
// reads instance state (this.width/this.canvas/etc.) throughout - a
// straight cut, not a rewrite. Called by Renderer.renderBackgroundCached(),
// which still lives in renderer.js itself as the thin caching wrapper
// Frozen module-level constants (Paradigma 1 & 7: Zero-Allocation & Layer Partitioning)
export const CITY_TOWERS = Object.freeze([
  { x: 0, w: 95, h: 62, antenna: true, beaconCol: '#ef4444', winCols: 7, winRows: 4, holoSign: false, heliPad: false },
  { x: 85, w: 90, h: 88, antenna: true, beaconCol: '#38bdf8', winCols: 8, winRows: 7, holoSign: true, heliPad: true },
  { x: 165, w: 120, h: 58, antenna: false, beaconCol: '#ef4444', winCols: 11, winRows: 4, holoSign: false, heliPad: false },
  { x: 275, w: 105, h: 96, antenna: true, beaconCol: '#ef4444', winCols: 9, winRows: 8, holoSign: true, heliPad: false },
  { x: 370, w: 135, h: 52, antenna: false, beaconCol: '#38bdf8', winCols: 12, winRows: 3, holoSign: false, heliPad: false }
]);

export const REFINERY_STACKS = Object.freeze([
  { x: 555, w: 32, h: 58, blinkColor: '#ef4444' },
  { x: 675, w: 26, h: 48, blinkColor: '#38bdf8' },
  { x: 745, w: 20, h: 38, blinkColor: '#ef4444' }
]);

export const TREE_CLUSTERS = Object.freeze([
  { x: 55, hScale: 0.9, wScale: 0.9, trunkH: 24, species: 'pine' },
  { x: 88, hScale: 1.25, wScale: 1.2, trunkH: 30, species: 'round' },
  { x: 118, hScale: 0.8, wScale: 0.85, trunkH: 22, species: 'pine' },
  { x: 235, hScale: 1.1, wScale: 1.05, trunkH: 28, species: 'crystal' },
  { x: 395, hScale: 0.85, wScale: 0.9, trunkH: 22, species: 'willow' },
  { x: 430, hScale: 1.3, wScale: 1.25, trunkH: 32, species: 'round' },
  { x: 470, hScale: 1.0, wScale: 1.0, trunkH: 26, species: 'pine' },
  { x: 725, hScale: 1.15, wScale: 1.1, trunkH: 28, species: 'crystal' },
  { x: 765, hScale: 0.75, wScale: 0.8, trunkH: 20, species: 'round' },
  { x: 1010, hScale: 1.2, wScale: 1.15, trunkH: 29, species: 'willow' },
  { x: 1045, hScale: 0.85, wScale: 0.9, trunkH: 23, species: 'pine' },
  { x: 1240, hScale: 0.95, wScale: 0.95, trunkH: 24, species: 'round' },
  { x: 1275, hScale: 1.35, wScale: 1.3, trunkH: 34, species: 'crystal' },
  { x: 1315, hScale: 1.05, wScale: 1.05, trunkH: 27, species: 'pine' },
  { x: 1515, hScale: 1.1, wScale: 1.1, trunkH: 28, species: 'willow' }
]);

let _sulNebulaCanvas = null;
function getSulNebulaCanvas() {
  if (!_sulNebulaCanvas && typeof document !== 'undefined') {
    _sulNebulaCanvas = document.createElement('canvas');
    _sulNebulaCanvas.width = 480;
    _sulNebulaCanvas.height = 480;
    const nctx = _sulNebulaCanvas.getContext('2d');
    const nebGrad = nctx.createRadialGradient(240, 240, 20, 240, 240, 240);
    nebGrad.addColorStop(0, 'rgba(217, 70, 239, 0.14)');
    nebGrad.addColorStop(0.5, 'rgba(147, 51, 234, 0.07)');
    nebGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    nctx.fillStyle = nebGrad;
    nctx.beginPath();
    nctx.arc(240, 240, 240, 0, Math.PI * 2);
    nctx.fill();
  }
  return _sulNebulaCanvas;
}

let _cachedMoonSheenGrad = null;
function getMoonSheenGrad(ctx) {
  if (!_cachedMoonSheenGrad) {
    _cachedMoonSheenGrad = ctx.createLinearGradient(-1, 0, 1, 0);
    _cachedMoonSheenGrad.addColorStop(0, 'rgba(224, 242, 254, 0)');
    _cachedMoonSheenGrad.addColorStop(0.3, 'rgba(224, 242, 254, 0.08)');
    _cachedMoonSheenGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.18)');
    _cachedMoonSheenGrad.addColorStop(0.7, 'rgba(224, 242, 254, 0.08)');
    _cachedMoonSheenGrad.addColorStop(1, 'rgba(224, 242, 254, 0)');
  }
  return _cachedMoonSheenGrad;
}

let _trafficStreakCanvases = null;
function getTrafficStreakCanvas(colorIdx, dir) {
  if (!_trafficStreakCanvases && typeof document !== 'undefined') {
    _trafficStreakCanvases = [];
    const colors = [
      ['rgba(0, 240, 255, 0.7)', 'rgba(0, 240, 255, 0)'],
      ['rgba(255, 0, 85, 0.7)', 'rgba(255, 0, 85, 0)'],
      ['rgba(250, 204, 21, 0.7)', 'rgba(250, 204, 21, 0)']
    ];
    for (let c = 0; c < 3; c++) {
      _trafficStreakCanvases[c] = {};
      const cEast = document.createElement('canvas');
      cEast.width = 64;
      cEast.height = 4;
      const ctxE = cEast.getContext('2d');
      const gradE = ctxE.createLinearGradient(64, 0, 0, 0);
      gradE.addColorStop(0, colors[c][0]);
      gradE.addColorStop(1, colors[c][1]);
      ctxE.fillStyle = gradE;
      ctxE.fillRect(0, 0, 64, 4);
      _trafficStreakCanvases[c][1] = cEast;

      const cWest = document.createElement('canvas');
      cWest.width = 64;
      cWest.height = 4;
      const ctxW = cWest.getContext('2d');
      const gradW = ctxW.createLinearGradient(0, 0, 64, 0);
      gradW.addColorStop(0, colors[c][0]);
      gradW.addColorStop(1, colors[c][1]);
      ctxW.fillStyle = gradW;
      ctxW.fillRect(0, 0, 64, 4);
      _trafficStreakCanvases[c][-1] = cWest;
    }
  }
  return _trafficStreakCanvases ? _trafficStreakCanvases[colorIdx % 3]?.[dir] : null;
}

// Ringed gas giant - atmospheric, soft-edged, night-sky integrated. Pulled
// out of renderStaticBackground() (was inline there, hardcoded to the
// battlefield's own canvas coordinates) so the exact same planet can be
// baked once onto a standalone <canvas> elsewhere (e.g. the pack-opening
// modal's Cargo Bay backdrop) instead of approximating it in CSS.
// Excludes the crepuscular god-rays pass, which needs a battlefield-specific
// ground reference (laneY) that doesn't exist outside the battlefield.
export function drawSpacePlanet(ctx, {
  x, y, radius,
  ringAngle = -0.34,
  quality = 'high',
  isMobile = false,
  t = 0,
  maxHazeRadius = null,
  theme = 'core',
  hasRings = (theme === 'core')
} = {}) {
  const planetX = x, planetY = y, planetR = radius;

  // Subtle organic breathing - same curve the battlefield uses (t is always
  // 0 there too: this render is baked once, not animated per frame).
  const moonFlicker = 0.82 + 0.22 * Math.sin(t * 1.25) + 0.08 * Math.sin(t * 3.6 + 1.4) + 0.04 * Math.sin(t * 8.7 + 2.1);

  // Layer 0: Deep nebula scatter - subtle, clamped strictly within canvas bounds
  const maxSafeHaze = Math.min(planetX, planetY) * 0.90;
  const hazeR = maxHazeRadius ? Math.min(maxHazeRadius, maxSafeHaze) : Math.min(planetR * 1.45, maxSafeHaze);
  const nebulaHaze = ctx.createRadialGradient(planetX, planetY, planetR * 0.6, planetX, planetY, hazeR);
  if (theme === 'human') {
    nebulaHaze.addColorStop(0,   `rgba(14, 116, 144, ${0.06 * moonFlicker})`);
    nebulaHaze.addColorStop(0.4, `rgba( 3, 105, 161, ${0.03 * moonFlicker})`);
    nebulaHaze.addColorStop(0.75,`rgba( 2,  50, 100, ${0.01 * moonFlicker})`);
  } else if (theme === 'sul') {
    nebulaHaze.addColorStop(0,   `rgba(147, 51, 234, ${0.06 * moonFlicker})`);
    nebulaHaze.addColorStop(0.4, `rgba(107, 33, 168, ${0.03 * moonFlicker})`);
    nebulaHaze.addColorStop(0.75,`rgba( 58, 12,  90, ${0.01 * moonFlicker})`);
  } else {
    nebulaHaze.addColorStop(0,   `rgba(160, 110, 60, ${0.06 * moonFlicker})`);
    nebulaHaze.addColorStop(0.4, `rgba(120,  80, 40, ${0.03 * moonFlicker})`);
    nebulaHaze.addColorStop(0.75,`rgba( 80,  50, 25, ${0.01 * moonFlicker})`);
  }
  nebulaHaze.addColorStop(1,   'rgba(0, 0, 0, 0)');
  ctx.fillStyle = nebulaHaze;
  ctx.beginPath(); ctx.arc(planetX, planetY, hazeR, 0, Math.PI * 2); ctx.fill();

  // Layer 1: Mid corona - soft, subtle atmospheric halo (never exceeds canvas)
  const coronaR = Math.min(planetR * 1.28, hazeR);
  const corona1 = ctx.createRadialGradient(planetX, planetY, planetR * 0.92, planetX, planetY, coronaR);
  if (theme === 'human') {
    corona1.addColorStop(0,   `rgba(  0, 229, 255, ${0.08 * moonFlicker})`);
    corona1.addColorStop(0.45,`rgba(  2, 132, 199, ${0.03 * moonFlicker})`);
  } else if (theme === 'sul') {
    corona1.addColorStop(0,   `rgba(217,  70, 239, ${0.08 * moonFlicker})`);
    corona1.addColorStop(0.45,`rgba(147,  51, 234, ${0.03 * moonFlicker})`);
  } else {
    corona1.addColorStop(0,   `rgba(200, 150,  80, ${0.07 * moonFlicker})`);
    corona1.addColorStop(0.45,`rgba(170, 120,  55, ${0.03 * moonFlicker})`);
  }
  corona1.addColorStop(1,  'rgba(0, 0, 0, 0)');
  ctx.fillStyle = corona1;
  ctx.beginPath(); ctx.arc(planetX, planetY, coronaR, 0, Math.PI * 2); ctx.fill();

  ctx.save();

  // Back Ring Half (behind the planet body) - only if hasRings is true
  if (hasRings) {
    ctx.save();
    ctx.translate(planetX, planetY);
    ctx.rotate(ringAngle);
    ctx.beginPath();
    ctx.rect(-planetR * 3.0, -planetR * 3.0, planetR * 6.0, planetR * 3.0);
    ctx.clip();
    ctx.strokeStyle = 'rgba(180, 140, 90, 0.22)';
    ctx.lineWidth = 14;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 2.4, planetR * 0.50, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(220, 175, 120, 0.40)';
    ctx.lineWidth = 9;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 2.0, planetR * 0.42, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(240, 210, 140, 0.50)';
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 1.55, planetR * 0.32, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  // Planet Body
  const planetGrad = ctx.createRadialGradient(
    planetX - planetR * 0.30, planetY - planetR * 0.30, planetR * 0.08,
    planetX, planetY, planetR
  );
  if (theme === 'human') {
    planetGrad.addColorStop(0,    'rgba( 56, 189, 248, 0.95)');
    planetGrad.addColorStop(0.30, 'rgba( 14, 165, 233, 0.90)');
    planetGrad.addColorStop(0.60, 'rgba(  3, 105, 161, 0.86)');
    planetGrad.addColorStop(0.88, 'rgba( 15,  23,  42, 0.92)');
    planetGrad.addColorStop(1,    'rgba(  4,   9,  20, 0.96)');
  } else if (theme === 'sul') {
    planetGrad.addColorStop(0,    'rgba(244, 114, 182, 0.95)');
    planetGrad.addColorStop(0.30, 'rgba(192, 132, 252, 0.90)');
    planetGrad.addColorStop(0.60, 'rgba(126,  34, 206, 0.86)');
    planetGrad.addColorStop(0.88, 'rgba( 59,   7, 100, 0.92)');
    planetGrad.addColorStop(1,    'rgba( 20,   4,  35, 0.96)');
  } else {
    planetGrad.addColorStop(0,    'rgba(200, 165,  85, 0.92)');
    planetGrad.addColorStop(0.30, 'rgba(170, 125,  60, 0.88)');
    planetGrad.addColorStop(0.60, 'rgba(115,  75,  30, 0.85)');
    planetGrad.addColorStop(0.88, 'rgba( 55,  30,  12, 0.90)');
    planetGrad.addColorStop(1,    'rgba( 18,   8,   2, 0.95)');
  }
  ctx.fillStyle = planetGrad;
  ctx.beginPath(); ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2); ctx.fill();

  // Planetary surface structure / texture
  ctx.save();
  ctx.beginPath(); ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2); ctx.clip();

  if (theme === 'human') {
    // -------------------------------------------------------------
    // HUMAN VANGUARD: Abstract-Realistic Oceanic Fluid Dynamics
    // Tilted axial rotation (~22°), Coriolis fluid shear, deep ocean
    // basins, and elongated high-altitude storm eddies.
    // -------------------------------------------------------------
    ctx.save();
    ctx.translate(planetX, planetY);
    ctx.rotate(0.38); // 22° axial tilt

    // 1. Abyssal Ocean Basins & Continental Shelf Gradients
    const abyssalGrad = ctx.createRadialGradient(
      -planetR * 0.15, planetR * 0.10, planetR * 0.08,
      -planetR * 0.15, planetR * 0.10, planetR * 0.85
    );
    abyssalGrad.addColorStop(0,   'rgba( 2,  24,  64, 0.48)');
    abyssalGrad.addColorStop(0.55,'rgba( 3,  45, 105, 0.26)');
    abyssalGrad.addColorStop(0.85,'rgba(14, 116, 144, 0.12)');
    abyssalGrad.addColorStop(1,   'rgba( 2,  24,  64, 0)');
    ctx.fillStyle = abyssalGrad;
    ctx.fillRect(-planetR, -planetR, planetR * 2, planetR * 2);

    // 2. Fluid Shearing Belts (Tilted Coriolis Atmospheric Wave Fronts)
    const fluidBands = [
      { y: -0.68, h: 0.18, color: 'rgba(186, 230, 253, 0.12)' },
      { y: -0.42, h: 0.24, color: 'rgba( 14, 165, 233, 0.14)' },
      { y: -0.16, h: 0.16, color: 'rgba(224, 242, 254, 0.18)' },
      { y:  0.08, h: 0.28, color: 'rgba(  2, 132, 199, 0.12)' },
      { y:  0.38, h: 0.16, color: 'rgba(186, 230, 253, 0.15)' },
      { y:  0.62, h: 0.22, color: 'rgba( 56, 189, 248, 0.10)' }
    ];
    for (const band of fluidBands) {
      ctx.save();
      ctx.beginPath();
      const startY = band.y * planetR;
      const bandH = band.h * planetR;
      ctx.moveTo(-planetR * 1.1, startY);
      ctx.bezierCurveTo(
        -planetR * 0.5, startY + planetR * 0.04,
         planetR * 0.3, startY - planetR * 0.05,
         planetR * 1.1, startY + planetR * 0.03
      );
      ctx.lineTo(planetR * 1.1, startY + bandH);
      ctx.bezierCurveTo(
         planetR * 0.3, startY + bandH - planetR * 0.04,
        -planetR * 0.5, startY + bandH + planetR * 0.05,
        -planetR * 1.1, startY + bandH
      );
      ctx.closePath();
      ctx.fillStyle = band.color;
      ctx.fill();
      ctx.restore();
    }

    // 3. Elongated Jetstream Vortices (Great Storm Eddies)
    // Primary Equatorial Storm Vortex
    ctx.save();
    ctx.translate(-planetR * 0.22, -planetR * 0.14);
    ctx.rotate(-0.16);
    ctx.scale(1.8, 0.95);
    const vortexGrad = ctx.createRadialGradient(0, 0, planetR * 0.03, 0, 0, planetR * 0.18);
    vortexGrad.addColorStop(0,    'rgba(  2,  40,  95, 0.42)');
    vortexGrad.addColorStop(0.55, 'rgba( 14, 165, 233, 0.22)');
    vortexGrad.addColorStop(0.85, 'rgba(224, 242, 254, 0.20)');
    vortexGrad.addColorStop(1,    'rgba(224, 242, 254, 0)');
    ctx.fillStyle = vortexGrad;
    ctx.beginPath();
    ctx.arc(0, 0, planetR * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // High-Altitude Cirrus Streak Companion
    ctx.save();
    ctx.translate(planetR * 0.24, planetR * 0.20);
    ctx.rotate(0.14);
    ctx.scale(2.2, 0.75);
    const cirrusGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, planetR * 0.14);
    cirrusGrad.addColorStop(0,   'rgba(255, 255, 255, 0.24)');
    cirrusGrad.addColorStop(0.5, 'rgba(224, 242, 254, 0.12)');
    cirrusGrad.addColorStop(1,   'rgba(224, 242, 254, 0)');
    ctx.fillStyle = cirrusGrad;
    ctx.beginPath();
    ctx.arc(0, 0, planetR * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 4. Polar Atmosphere Hood
    const polarGrad = ctx.createRadialGradient(0, -planetR * 0.95, 0, 0, -planetR * 0.95, planetR * 0.45);
    polarGrad.addColorStop(0,   'rgba(240, 249, 255, 0.26)');
    polarGrad.addColorStop(0.6, 'rgba(186, 230, 253, 0.10)');
    polarGrad.addColorStop(1,   'rgba(186, 230, 253, 0)');
    ctx.fillStyle = polarGrad;
    ctx.beginPath();
    ctx.arc(0, -planetR * 0.95, planetR * 0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

  } else if (theme === 'sul') {
    // -------------------------------------------------------------
    // SUL SWARM: Abstract-Realistic Alien Geothermal Tectonics
    // Reverse axial tilt (~ -28°), smoked plum basalt crust,
    // incandescent mantle fissures, and diffuse bioplasmic vapor.
    // -------------------------------------------------------------
    ctx.save();
    ctx.translate(planetX, planetY);
    ctx.rotate(-0.48); // -28° reverse axial tilt

    // 1. Basaltic Basins & Crustal Density Variations
    const crustGrad = ctx.createRadialGradient(
      -planetR * 0.18, planetR * 0.12, planetR * 0.12,
      -planetR * 0.18, planetR * 0.12, planetR * 0.85
    );
    crustGrad.addColorStop(0,   'rgba( 22,   6,  34, 0.58)');
    crustGrad.addColorStop(0.55,'rgba( 55,  12,  85, 0.32)');
    crustGrad.addColorStop(1,   'rgba( 22,   6,  34, 0)');
    ctx.fillStyle = crustGrad;
    ctx.fillRect(-planetR, -planetR, planetR * 2, planetR * 2);

    // 2. Diffuse Sub-Surface Geothermal Plumes (Glowing Mantle Chambers)
    const plumes = [
      { x: -0.22, y: -0.20, r: 0.28, color: 'rgba(236,  72, 153, 0.26)' },
      { x:  0.26, y:  0.18, r: 0.32, color: 'rgba(217,  70, 239, 0.22)' }
    ];
    for (const p of plumes) {
      const px = p.x * planetR;
      const py = p.y * planetR;
      const pr = p.r * planetR;
      const plumeGrad = ctx.createRadialGradient(px, py, 0, px, py, pr);
      plumeGrad.addColorStop(0,   p.color);
      plumeGrad.addColorStop(0.5, 'rgba(168,  85, 247, 0.10)');
      plumeGrad.addColorStop(1,   'rgba(147,  51, 234, 0)');
      ctx.fillStyle = plumeGrad;
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
    }

    // 3. Planetary Tectonic Fractures / Incandescent Mantle Fissures
    // Primary Grand Rift
    ctx.save();
    ctx.lineCap = 'round';
    // Fissure Halo / Thermal Bleed
    ctx.strokeStyle = 'rgba(217, 70, 239, 0.26)';
    ctx.lineWidth = planetR * 0.045;
    ctx.beginPath();
    ctx.moveTo(-planetR * 0.75, -planetR * 0.35);
    ctx.bezierCurveTo(
      -planetR * 0.30, -planetR * 0.15,
       planetR * 0.10, -planetR * 0.30,
       planetR * 0.70,  planetR * 0.10
    );
    ctx.stroke();

    // Fissure Core Luminous Filament
    ctx.strokeStyle = 'rgba(253, 230, 250, 0.75)';
    ctx.lineWidth = planetR * 0.012;
    ctx.beginPath();
    ctx.moveTo(-planetR * 0.75, -planetR * 0.35);
    ctx.bezierCurveTo(
      -planetR * 0.30, -planetR * 0.15,
       planetR * 0.10, -planetR * 0.30,
       planetR * 0.70,  planetR * 0.10
    );
    ctx.stroke();

    // Secondary Transverse Fracture
    ctx.strokeStyle = 'rgba(244, 114, 182, 0.24)';
    ctx.lineWidth = planetR * 0.035;
    ctx.beginPath();
    ctx.moveTo(-planetR * 0.10, -planetR * 0.22);
    ctx.bezierCurveTo(
       planetR * 0.05,  planetR * 0.05,
      -planetR * 0.08,  planetR * 0.28,
       planetR * 0.15,  planetR * 0.55
    );
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255, 235, 250, 0.65)';
    ctx.lineWidth = planetR * 0.010;
    ctx.beginPath();
    ctx.moveTo(-planetR * 0.10, -planetR * 0.22);
    ctx.bezierCurveTo(
       planetR * 0.05,  planetR * 0.05,
      -planetR * 0.08,  planetR * 0.28,
       planetR * 0.15,  planetR * 0.55
    );
    ctx.stroke();
    ctx.restore();

    // 4. Tilted Bioplasmic Vapor Bands (Smoky atmospheric stratification)
    const sulBands = [
      { y: -0.52, h: 0.20, color: 'rgba(147,  51, 234, 0.11)' },
      { y: -0.15, h: 0.24, color: 'rgba(107,  33, 168, 0.13)' },
      { y:  0.25, h: 0.22, color: 'rgba(192, 132, 252, 0.09)' }
    ];
    for (const band of sulBands) {
      ctx.save();
      ctx.beginPath();
      const startY = band.y * planetR;
      const bandH = band.h * planetR;
      ctx.moveTo(-planetR * 1.1, startY);
      ctx.bezierCurveTo(
        -planetR * 0.4, startY - planetR * 0.04,
         planetR * 0.4, startY + planetR * 0.05,
         planetR * 1.1, startY - planetR * 0.03
      );
      ctx.lineTo(planetR * 1.1, startY + bandH);
      ctx.bezierCurveTo(
         planetR * 0.4, startY + bandH + planetR * 0.04,
        -planetR * 0.4, startY + bandH - planetR * 0.05,
        -planetR * 1.1, startY + bandH
      );
      ctx.closePath();
      ctx.fillStyle = band.color;
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  } else {
    // -------------------------------------------------------------
    // CORE DECK: Classic Gas Giant Atmospheric Storm Bands (Unchanged)
    // -------------------------------------------------------------
    const bandColors = [
      'rgba(140, 65,  10, 0.18)', 'rgba(190, 120, 40, 0.14)',
      'rgba( 30,100,140, 0.10)', 'rgba( 90, 40, 10, 0.20)',
      'rgba(200, 180, 90, 0.13)'
    ];
    for (let b = 0; b < 10; b++) {
      const by = planetY - planetR + b * (planetR * 2 / 10);
      ctx.fillStyle = bandColors[b % bandColors.length];
      ctx.fillRect(planetX - planetR, by, planetR * 2, planetR * 0.22);
    }
  }

  // Terminator - night-side shadow
  const termGrad = ctx.createRadialGradient(
    planetX + planetR * 0.38, planetY + planetR * 0.38, planetR * 0.20,
    planetX, planetY, planetR
  );
  termGrad.addColorStop(0,    'rgba(2, 4, 14, 0.88)');
  termGrad.addColorStop(0.58, 'rgba(2, 4, 14, 0.42)');
  termGrad.addColorStop(0.85, 'rgba(2, 4, 14, 0.10)');
  termGrad.addColorStop(1,    'rgba(2, 4, 14, 0)');
  ctx.fillStyle = termGrad;
  ctx.beginPath(); ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Atmospheric limb glow - tight, subtle planetary atmosphere
  const limbR = planetR * 1.08;
  const limbGrad = ctx.createRadialGradient(planetX, planetY, planetR * 0.82, planetX, planetY, limbR);
  limbGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
  if (theme === 'human') {
    limbGrad.addColorStop(0.65, `rgba(  0, 229, 255, ${0.05 * moonFlicker})`);
    limbGrad.addColorStop(0.88, `rgba( 56, 189, 248, ${0.08 * moonFlicker})`);
  } else if (theme === 'sul') {
    limbGrad.addColorStop(0.65, `rgba(217,  70, 239, ${0.05 * moonFlicker})`);
    limbGrad.addColorStop(0.88, `rgba(168,  85, 247, ${0.08 * moonFlicker})`);
  } else {
    limbGrad.addColorStop(0.65, `rgba(100, 140, 200, ${0.04 * moonFlicker})`);
    limbGrad.addColorStop(0.88, `rgba( 80, 110, 180, ${0.06 * moonFlicker})`);
  }
  limbGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = limbGrad;
  ctx.beginPath(); ctx.arc(planetX, planetY, limbR, 0, Math.PI * 2); ctx.fill();

  // Atmospheric edge diffusion - seamlessly fades to 0 to prevent any hard circular edge
  if (quality !== 'low') {
    const maxPasses = isMobile ? 1 : 3;
    for (let pass = 0; pass < maxPasses; pass++) {
      const pr = planetR + (pass + 1) * 2.5;
      const pa = (0.06 - pass * 0.018) * moonFlicker;
      const edgeGrad = ctx.createRadialGradient(planetX, planetY, planetR * 0.94, planetX, planetY, pr);
      edgeGrad.addColorStop(0,   'rgba(0, 0, 0, 0)');
      if (theme === 'human') {
        edgeGrad.addColorStop(0.5, `rgba(14, 116, 144, ${pa * 0.4})`);
        edgeGrad.addColorStop(1,   'rgba(3, 105, 161, 0)');
      } else if (theme === 'sul') {
        edgeGrad.addColorStop(0.5, `rgba(147, 51, 234, ${pa * 0.4})`);
        edgeGrad.addColorStop(1,   'rgba(88, 28, 135, 0)');
      } else {
        edgeGrad.addColorStop(0.5, `rgba(90, 60, 25, ${pa * 0.4})`);
        edgeGrad.addColorStop(1,   'rgba(50, 30, 10, 0)');
      }
      ctx.fillStyle = edgeGrad;
      ctx.beginPath(); ctx.arc(planetX, planetY, pr, 0, Math.PI * 2); ctx.fill();
    }
  }

  // Front Ring Half (over the planet body) - only if hasRings is true
  if (hasRings) {
    ctx.save();
    ctx.translate(planetX, planetY);
    ctx.rotate(ringAngle);
    ctx.beginPath();
    ctx.rect(-planetR * 3.0, 0, planetR * 6.0, planetR * 3.0);
    ctx.clip();
    ctx.strokeStyle = 'rgba(180, 140, 90, 0.28)';
    ctx.lineWidth = 14;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 2.4, planetR * 0.50, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(220, 175, 120, 0.52)';
    ctx.lineWidth = 9;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 2.0, planetR * 0.42, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(240, 210, 140, 0.62)';
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.ellipse(0, 0, planetR * 1.55, planetR * 0.32, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
}

let _cloudShadowLUT = null;
export function getCloudShadowLUT() {
  if (_cloudShadowLUT) return _cloudShadowLUT;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const grad = ctx.createRadialGradient(32, 16, 2, 32, 16, 32);
  grad.addColorStop(0, 'rgba(2, 6, 23, 0.48)');
  grad.addColorStop(0.6, 'rgba(2, 6, 23, 0.24)');
  grad.addColorStop(1, 'rgba(2, 6, 23, 0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(32, 16, 32, 16, 0, 0, Math.PI * 2);
  ctx.fill();
  _cloudShadowLUT = canvas;
  return _cloudShadowLUT;
}

export const backgroundMixin = {
  renderStaticBackground(ctx, battlefield, customBounds = null) {
    const t = 0;
    const laneY = battlefield ? battlefield.laneY : 176;
    const laneH = battlefield ? battlefield.laneHeight : 320;

    // Visible bounds from camera or custom bounds to eliminate black bars on any aspect ratio / screen size
    const bounds = customBounds || (this.viewport ? this.viewport.getVisibleBounds() : null);
    const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
    const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (this.height + 40))) : (this.height + 40);
    const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
    const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (this.width + 40))) : (this.width + 40);

    const visibleTop = customBounds ? rawTop : Math.min(0, rawTop - 20);
    const visibleBottom = customBounds ? rawBottom : Math.max(this.height, rawBottom + 20);
    const visibleLeft = customBounds ? rawLeft : Math.min(0, rawLeft - 20);
    const visibleRight = customBounds ? rawRight : Math.max(this.width, rawRight + 20);
    const bgWidth = visibleRight - visibleLeft;

    const floorRowH = laneH / 10;
    const floorTopY = laneY + floorRowH;
    const skyBottom = floorTopY + 2;

    // Solid opaque base layer across the entire world bounding box (eliminates transparent gaps)
    ctx.fillStyle = '#020308';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, visibleBottom - visibleTop);

    // 1. SKY & DEEP SPACE HORIZON (y = visibleTop to skyBottom)
    const skyGrad = ctx.createLinearGradient(0, visibleTop, 0, skyBottom);
    skyGrad.addColorStop(0, '#000104');
    skyGrad.addColorStop(0.4, '#05080d');
    skyGrad.addColorStop(0.75, '#0f1411');
    skyGrad.addColorStop(1, '#1d231a');

    ctx.fillStyle = skyGrad;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, skyBottom - visibleTop);

    // 🌌 1A. INTERSTELLAR DEEP SPACE NEBULAE (Parallax Cosmic Dust Clouds)
    // Western Cyan/Indigo Stellar Nursery
    const wNebula = ctx.createRadialGradient(280, 42, 10, 280, 42, 340);
    wNebula.addColorStop(0, 'rgba(6, 182, 212, 0.13)');
    wNebula.addColorStop(0.45, 'rgba(99, 102, 241, 0.08)');
    wNebula.addColorStop(0.8, 'rgba(30, 27, 75, 0.04)');
    wNebula.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = wNebula;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth * 0.6, skyBottom - visibleTop);

    // Eastern Magenta/Amber Supernova Remnant
    const eNebula = ctx.createRadialGradient(this.width - 260, 46, 10, this.width - 260, 46, 320);
    eNebula.addColorStop(0, 'rgba(217, 70, 239, 0.12)');
    eNebula.addColorStop(0.45, 'rgba(244, 63, 94, 0.07)');
    eNebula.addColorStop(0.8, 'rgba(245, 158, 11, 0.03)');
    eNebula.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = eNebula;
    ctx.fillRect(visibleLeft + bgWidth * 0.4, visibleTop, bgWidth * 0.6, skyBottom - visibleTop);


    // 🌆 1A-2. DISTANT CYBERPUNK CITY & SKY-TRAFFIC (backmost background
    // layer - sits behind the mountains/refinery/spire skyline drawn
    // further down, visible only through the gaps between them and above
    // their rooflines, which is what actually reads as "distant" instead
    // of competing with the nearer silhouette). Brought back from the
    // since-reverted cyberpunk redesign per chat request, scaled down
    // (~65% height, muted color) so it stays clearly behind everything
    // else rather than fighting it for attention.
    ctx.save();
    const cityBuildingCount = 22;
    const cityColW = this.width / cityBuildingCount;
    for (let i = 0; i < cityBuildingCount; i++) {
      const seed = (i * 7919 + 13) % 97;
      const bw = cityColW * 0.62;
      const bh = 16 + (seed % 5) * 8; // ~16-48px, well under the ~86px mountain peaks
      const bx = i * cityColW + (cityColW - bw) / 2;
      const by = laneY - bh;

      const cityBodyGrad = ctx.createLinearGradient(bx, by, bx, laneY);
      cityBodyGrad.addColorStop(0, 'rgba(38, 52, 78, 0.88)');
      cityBodyGrad.addColorStop(1, 'rgba(12, 16, 30, 0.88)');
      ctx.fillStyle = cityBodyGrad;
      ctx.fillRect(bx, by, bw, bh);

      // Antenna needle on roughly a third of the towers
      if (seed % 3 === 0) {
        ctx.fillStyle = 'rgba(40, 58, 82, 0.8)';
        ctx.fillRect(bx + bw / 2 - 1, by - 8, 2, 8);
      }

      // Lit window grid, muted for distance but bright enough to actually
      // read as windows rather than disappearing into the ridge haze.
      // Low quality draws a single light band; mobile medium halves window step.
      if (this.quality === 'low') {
        if (seed % 2 === 0) {
          ctx.fillStyle = 'rgba(56, 189, 248, 0.4)';
          ctx.fillRect(bx + 3, by + 4, bw - 6, 2);
        }
      } else {
        const colStep = (this.isMobile && this.quality === 'medium') ? 2 : 1;
        const cols = Math.max(1, Math.floor(bw / 7));
        const rows = Math.max(1, Math.floor(bh / 7));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c += colStep) {
            const winSeed = (i * 31 + r * 11 + c * 17);
            if (winSeed % 3 !== 0) continue;
            const winColor = (winSeed % 5 === 0) ? 'rgba(56, 189, 248, 0.65)' : ((winSeed % 7 === 0) ? 'rgba(250, 204, 21, 0.6)' : 'rgba(226, 232, 240, 0.5)');
            ctx.fillStyle = winColor;
            ctx.fillRect(bx + 2 + c * 7, by + 2 + r * 7, 3, 2.5);
          }
        }
      }
    }

    ctx.restore();


    // 🪐 Ringed Gas Giant – atmospheric, soft-edged, night-sky integrated.
    // Drawing itself lives in drawSpacePlanet() (shared with the pack-opening
    // modal's backdrop) - only the battlefield-specific placement and the
    // god-rays below (which need laneY) stay inline here.
    const planetX = 1220;
    const planetY = 55;
    const planetR = 54;
    const ringAngle = -0.34; // radians
    const moonFlicker = 0.82 + 0.22 * Math.sin(t * 1.25) + 0.08 * Math.sin(t * 3.6 + 1.4) + 0.04 * Math.sin(t * 8.7 + 2.1);
    // Used by floor moonbeam / plate sheen further down – name kept as-is
    const moonSway = Math.sin(t * 0.85) * 22;

    drawSpacePlanet(ctx, { x: planetX, y: planetY, radius: planetR, ringAngle, quality: this.quality, isMobile: this.isMobile, t });

    // ── 1C. Crepuscular God-Rays – toned down to match the softer planet (skipped on low) ───
    if (this.quality !== 'low') {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const rayBaseAlpha = (0.032 + 0.016 * Math.sin(t * 1.6)) * moonFlicker;
      const rayScale = planetR / 28;
      const godRays = this.isMobile ? [
        { topOffset:  -8 * rayScale, botOffset: -160, wTop: 12 * rayScale, wBot: 110, alphaMul: 1.0 },
        { topOffset:  12 * rayScale, botOffset:   40, wTop: 14 * rayScale, wBot: 125, alphaMul: 1.1 }
      ] : [
        { topOffset: -24 * rayScale, botOffset: -320, wTop: 8 * rayScale, wBot: 84, alphaMul: 0.8 },
        { topOffset:  -8 * rayScale, botOffset: -160, wTop: 12 * rayScale, wBot: 110, alphaMul: 1.0 },
        { topOffset:  12 * rayScale, botOffset:   40, wTop: 14 * rayScale, wBot: 125, alphaMul: 1.1 },
        { topOffset:  30 * rayScale, botOffset:  240, wTop: 10 * rayScale, wBot:  95, alphaMul: 0.9 },
        { topOffset:  46 * rayScale, botOffset:  410, wTop:  6 * rayScale, wBot:  70, alphaMul: 0.65 }
      ];
      for (const gr of godRays) {
        const rx1  = planetX + gr.topOffset - gr.wTop / 2;
        const rx2  = planetX + gr.topOffset + gr.wTop / 2;
        const rbx1 = planetX + gr.botOffset - gr.wBot / 2;
        const rbx2 = planetX + gr.botOffset + gr.wBot / 2;
        const rGrad = ctx.createLinearGradient(planetX, planetY, planetX + gr.botOffset, laneY + 40);
        rGrad.addColorStop(0,    `rgba(230, 190, 130, ${rayBaseAlpha * gr.alphaMul * 1.4})`);
        rGrad.addColorStop(0.35, `rgba(190, 150, 100, ${rayBaseAlpha * gr.alphaMul * 0.7})`);
        rGrad.addColorStop(0.8,  `rgba(140, 100,  60, ${rayBaseAlpha * gr.alphaMul * 0.2})`);
        rGrad.addColorStop(1,    'rgba(0, 0, 0, 0)');
        ctx.fillStyle = rGrad;
        ctx.beginPath();
        ctx.moveTo(rx1, planetY);
        ctx.lineTo(rx2, planetY);
        ctx.lineTo(rbx2, laneY + 40);
        ctx.lineTo(rbx1, laneY + 40);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }


    // 🏞️ 1D. DISTANT ATMOSPHERIC PARALLAX RIDGE (deep background silhouette layer for extra depth)
    // Sits strictly behind the foreground horizon structures and is deliberately hazier/flatter/
    // bluer than everything in front of it, so the eye reads it as farther away.
    ctx.fillStyle = 'rgba(51, 65, 85, 0.32)';
    ctx.beginPath();
    ctx.moveTo(visibleLeft, laneY);
    for (let x = visibleLeft; x <= visibleRight; x += 22) {
      const ridgeH = 30 + Math.sin(x * 0.008 + 1.3) * 13 + Math.sin(x * 0.021 + 4) * 7;
      ctx.lineTo(x, laneY - ridgeH);
    }
    ctx.lineTo(visibleRight, laneY);
    ctx.closePath();
    ctx.fill();
    // Faint haze wash to sell atmospheric perspective (distant = lighter/less saturated)
    const distHaze = ctx.createLinearGradient(0, laneY - 62, 0, laneY);
    distHaze.addColorStop(0, 'rgba(148, 163, 184, 0)');
    distHaze.addColorStop(1, 'rgba(148, 163, 184, 0.09)');
    ctx.fillStyle = distHaze;
    ctx.fillRect(visibleLeft, laneY - 62, bgWidth, 62);

    // 🏔️ HIGH-FIDELITY ASYMMETRIC SKYLINE & ALIEN HORIZON
    // 🏙️ LEFT ZONE (0-500px): Human Industrial City Towers, Illuminated Windows & Dynamic Events
    const cityTowers = [
      { x: 0, w: 95, h: 62, antenna: true, beaconCol: '#ef4444', winCols: 7, winRows: 4, holoSign: false, heliPad: false },
      { x: 85, w: 90, h: 88, antenna: true, beaconCol: '#38bdf8', winCols: 8, winRows: 7, holoSign: true, heliPad: true },
      { x: 165, w: 120, h: 58, antenna: false, beaconCol: '#ef4444', winCols: 11, winRows: 4, holoSign: false, heliPad: false },
      { x: 275, w: 105, h: 96, antenna: true, beaconCol: '#ef4444', winCols: 9, winRows: 8, holoSign: true, heliPad: false },
      { x: 370, w: 135, h: 52, antenna: false, beaconCol: '#38bdf8', winCols: 12, winRows: 3, holoSign: false, heliPad: false }
    ];

    for (let idx = 0; idx < cityTowers.length; idx++) {
      const tower = cityTowers[idx];
      const tx = tower.x;
      const tw = tower.w;
      const th = tower.h;
      const ty = laneY - th;

      // 1. Tower Silhouette Body & Beveled Cladding
      ctx.fillStyle = '#070b14';
      ctx.fillRect(tx, ty, tw, th);

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(tx + 4, ty + 4, tw - 8, th - 4);

      // Top Roof Ledge Highlight
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(tx + 2, ty, tw - 4, 3);
      ctx.fillStyle = 'rgba(56, 189, 248, 0.35)';
      ctx.fillRect(tx + 4, ty, tw - 8, 1);

      // 2. Vertical Structural Mullions / Recessed Architectural Columns
      ctx.fillStyle = '#1e293b';
      const colSpacing = tw / 3;
      ctx.fillRect(tx + colSpacing - 3, ty + 6, 6, th - 6);
      ctx.fillRect(tx + colSpacing * 2 - 3, ty + 6, 6, th - 6);
      ctx.fillStyle = '#334155';
      ctx.fillRect(tx + colSpacing - 1, ty + 6, 2, th - 6);
      ctx.fillRect(tx + colSpacing * 2 - 1, ty + 6, 2, th - 6);

      // 3. 🏢 MULTI-PANE ILLUMINATED WINDOWS MATRIX
      const cellW = (tw - 16) / tower.winCols;
      for (let r = 0; r < tower.winRows; r++) {
        const wy = ty + 10 + r * 9;
        if (wy > laneY - 12) continue;
        for (let c = 0; c < tower.winCols; c++) {
          const wx = tx + 8 + c * cellW;
          const wSeed = (idx * 97 + r * 19 + c * 31);

          // Roughly 45% of windows lit
          if (wSeed % 11 > 4) continue;

          // Occasional gentle flicker or light state change
          const flicker = Math.sin(t * 1.5 + wSeed * 0.8) > -0.75;
          if (!flicker) continue;

          let winColor = 'rgba(250, 204, 21, 0.72)'; // Warm Gold
          if (wSeed % 5 === 0) winColor = 'rgba(56, 189, 248, 0.80)'; // Cyber Cyan
          else if (wSeed % 7 === 0) winColor = 'rgba(241, 245, 249, 0.65)'; // Cold White
          else if (wSeed % 13 === 0) winColor = 'rgba(244, 63, 94, 0.75)'; // Red Security Terminal

          ctx.fillStyle = winColor;
          ctx.fillRect(wx + 1, wy, Math.max(2, cellW - 3), 3.5);
        }
      }

      // 4. 📡 ROOFTOP ANTENNAS & AIRCRAFT WARNING BEACONS
      if (tower.antenna) {
        const antX = tx + (idx % 2 === 0 ? 14 : tw - 16);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(antX - 1.5, ty - 18, 3, 18);
        ctx.fillStyle = '#64748b';
        ctx.fillRect(antX - 0.75, ty - 18, 1.5, 18);

        // Blinking warning beacon
        const beaconBlink = Math.sin(t * 4.5 + idx * 2.1) > 0.1;
        ctx.fillStyle = beaconBlink ? tower.beaconCol : '#1e293b';
        ctx.fillRect(antX - 2, ty - 21, 4, 3);
        if (beaconBlink) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(antX - 1, ty - 20, 2, 1);
        }
      }

      // 5. 🚁 ROOFTOP HELI-PAD / DRONE PLATFORM
      if (tower.heliPad) {
        const hPadX = tx + tw / 2;
        const hPadY = ty + 1;
        // Perimeter runway lights
        const hBlink = Math.floor(t * 2) % 2 === 0;
        ctx.fillStyle = hBlink ? '#4ade80' : '#15803d';
        ctx.fillRect(hPadX - 12, hPadY - 2, 2, 2);
        ctx.fillRect(hPadX + 10, hPadY - 2, 2, 2);
        ctx.fillStyle = '#38bdf8';
        ctx.fillRect(hPadX - 4, hPadY - 1, 8, 1.5);
      }

      // 6. 🪧 HOLOGRAPHIC TECH SIGN / DATA LOGO
      if (tower.holoSign) {
        const signX = tx + 12;
        const signY = ty + 24;
        const signPulse = 0.65 + 0.35 * Math.sin(t * 3.2 + idx);
        ctx.fillStyle = `rgba(6, 182, 212, ${0.45 * signPulse})`;
        ctx.fillRect(signX, signY, 26, 8);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.8 * signPulse})`;
        ctx.fillRect(signX + 2, signY + 2, 10, 1.5);
        ctx.fillRect(signX + 2, signY + 5, 20, 1.5);
      }
    }

    // 🚀 DYNAMIC RANDOM EVENTS IN LEFT ZONE (0-500px)
    // Drone/cargo-speeder flight, rooftop arc-welding sparks, and the sky
    // searchlight sweep are all purely time-driven (no static "parked" pose
    // worth keeping) - rendered live in renderDynamicBackground().

    // Center-Left Zone (500-850px): Distant Industrial Refinery Valley & Cooling Stacks
    ctx.fillStyle = '#0a101d';
    ctx.fillRect(500, laneY - 26, 350, 26);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(508, laneY - 22, 334, 22);

    // Refinery Cooling Towers with Cladding (the warning strobe beacon &
    // billowing vapor plume are time-varying - rendered live in
    // renderDynamicBackground())
    for (const st of REFINERY_STACKS) {
      // Tower Body
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(st.x - 2, laneY - st.h, st.w + 4, st.h);
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(st.x, laneY - st.h + 3, st.w, st.h - 3);
      ctx.fillStyle = '#334155';
      ctx.fillRect(st.x + 3, laneY - st.h + 6, st.w - 6, st.h - 10);
    }

    // Heavy Industrial High-Tension Energy Pipelines (the glowing coolant
    // pulse racing through it is time-varying - rendered live in
    // renderDynamicBackground())
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(510, laneY - 34, 280, 6);
    ctx.fillStyle = '#334155';
    ctx.fillRect(510, laneY - 34, 280, 2);

    // Center-Right Zone (850-1250px): Giant Human Orbital Elevator Space Spire
    ctx.fillStyle = '#090e18';
    ctx.fillRect(940, laneY - 118, 28, 118); // Towering Elevator Core
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(943, laneY - 114, 22, 114);

    // Diamond Structural Truss Girders along Spire
    for (let gy = laneY - 110; gy < laneY - 20; gy += 14) {
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(942, gy);
      ctx.lineTo(966, gy + 14);
      ctx.moveTo(966, gy);
      ctx.lineTo(942, gy + 14);
      ctx.stroke();
    }

    // Elevator Hub Stations & Anchor Cables
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(934, laneY - 76, 40, 16); // Mid-hub
    ctx.fillStyle = '#334155';
    ctx.fillRect(934, laneY - 76, 40, 3);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(928, laneY - 32, 52, 20); // Base-hub

    // Mag-Lift Cargo Gondola & Aircraft Warning Strobes are time-varying
    // (climb / blink) - rendered live in renderDynamicBackground().

    // Far-Right Zone (1250-1600px): Organic Sul Spires & Void Crystals
    // Floating Void Crystal (bob + rotating energy ring) has no
    // static "parked" pose worth keeping - rendered live in renderDynamicBackground().

    // Organic Sul Hive Spire Horns
    ctx.fillStyle = '#070b14';
    ctx.fillRect(1280, laneY - 62, 46, 62);
    ctx.fillRect(1325, laneY - 88, 36, 88); // Tall Hive Horn
    ctx.fillRect(1510, laneY - 72, 62, 72);

    // Chitinous Ridge (the pulsing creep veins on top of it are time-varying
    // - rendered live in renderDynamicBackground())
    ctx.fillStyle = '#1e1026';
    ctx.fillRect(1328, laneY - 84, 30, 84);

    // Alien Trees along the Upper Lane Ridge (Trunks baked statically; foliage rendered dynamically in renderDynamicBackground)
    for (let idx = 0; idx < TREE_CLUSTERS.length; idx++) {
      const tree = TREE_CLUSTERS[idx];
      const tx = tree.x;
      const tH = tree.trunkH;
      ctx.fillStyle = '#451a03';
      ctx.fillRect(tx, laneY - tH, 6, tH);
    }

    // ✈️🛰️🚂 SPORADIC BACKGROUND TRAFFIC (satellite / airplane / far horizon train)
    // Each has its own cycle length/phase and is purely event-driven (no
    // static "parked" pose) - some cycles even evaluate false at a frozen
    // t=0, meaning they'd never render at all if left baked here. Rendered
    // live in renderDynamicBackground() instead, in front of the horizon
    // silhouettes as originally intended.

    // 🌫️ ATMOSPHERIC DEPTH FOG — a soft haze band sitting right at the base
    // of the skyline/mountains, fading their lower portion into the
    // horizon. Brought back from the since-reverted interim version per
    // chat request ("Tiefen-Nebel-Effekt... trägt dazu bei, dass der
    // Vordergrund sich mehr abhebt") - without it the background and the
    // floor blend into each other with nothing selling the distance
    // between them. Drawn last, over every skyline zone above, so it
    // fades all of them uniformly regardless of which zone sits where.
    const fogBandH = 85;
    const fogBottomY = laneY + laneH / 10;
    const floorH = laneH - floorRowH;

    // Cache static floor gradients (Paradigm 3 & 7: zero per-frame shader recompilation)
    if (!this._staticFloorGradients || this._staticFloorH !== floorH) {
      this._staticFloorH = floorH;
      const gMap = {};

      const depthFog = ctx.createLinearGradient(0, fogBottomY - fogBandH, 0, fogBottomY);
      depthFog.addColorStop(0, 'rgba(10, 16, 26, 0)');
      depthFog.addColorStop(0.5, 'rgba(16, 26, 40, 0.55)');
      depthFog.addColorStop(1, 'rgba(18, 28, 44, 0.88)');
      gMap.depthFog = depthFog;

      const horizonGlow = ctx.createLinearGradient(0, floorTopY - 20, 0, floorTopY + 3);
      horizonGlow.addColorStop(0, 'rgba(56, 189, 248, 0)');
      horizonGlow.addColorStop(0.6, 'rgba(94, 234, 212, 0.10)');
      horizonGlow.addColorStop(1, 'rgba(226, 232, 240, 0.30)');
      gMap.horizonGlow = horizonGlow;

      const floorGrad = ctx.createLinearGradient(0, floorTopY, 0, floorTopY + floorH);
      floorGrad.addColorStop(0, '#101620');    // Deep cool steel slate near horizon
      floorGrad.addColorStop(0.35, '#16202c'); // Brushed gunmetal alloy mid-plane
      floorGrad.addColorStop(0.7, '#1f2b3b');  // Specular titanium-steel lane center
      floorGrad.addColorStop(1, '#27364a');    // Illuminated front armored deck plate
      gMap.floorGrad = floorGrad;

      const wBaseLight = ctx.createRadialGradient(40, floorTopY + floorH * 0.5, 30, 40, floorTopY + floorH * 0.5, 680);
      wBaseLight.addColorStop(0, 'rgba(0, 240, 255, 0.24)');
      wBaseLight.addColorStop(0.35, 'rgba(14, 165, 233, 0.14)');
      wBaseLight.addColorStop(0.7, 'rgba(2, 132, 199, 0.04)');
      wBaseLight.addColorStop(1, 'rgba(0, 0, 0, 0)');
      gMap.wBaseLight = wBaseLight;

      const eBaseLight = ctx.createRadialGradient(this.width - 40, floorTopY + floorH * 0.5, 30, this.width - 40, floorTopY + floorH * 0.5, 680);
      eBaseLight.addColorStop(0, 'rgba(255, 0, 60, 0.24)');
      eBaseLight.addColorStop(0.35, 'rgba(217, 70, 239, 0.14)');
      eBaseLight.addColorStop(0.7, 'rgba(147, 51, 234, 0.04)');
      eBaseLight.addColorStop(1, 'rgba(0, 0, 0, 0)');
      gMap.eBaseLight = eBaseLight;

      this._staticFloorGradients = gMap;
    }
    const { depthFog, horizonGlow, floorGrad, wBaseLight, eBaseLight } = this._staticFloorGradients;

    ctx.fillStyle = depthFog;
    ctx.fillRect(visibleLeft, fogBottomY - fogBandH, bgWidth, fogBandH);

    // 🌅 HORIZON CONTRAST RIM
    ctx.fillStyle = horizonGlow;
    ctx.fillRect(visibleLeft, floorTopY - 20, bgWidth, 23);
    ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
    ctx.fillRect(visibleLeft, floorTopY - 1, bgWidth, 1.5);

    // 2. 3D ANGLED ISOMETRIC BATTLEFIELD FLOOR (The Lane: floorTopY to laneY + laneH)
    ctx.fillStyle = floorGrad;
    ctx.fillRect(visibleLeft, floorTopY, bgWidth, floorH);

    // 🌟 2A-1. BRUSHED METAL ANISOTROPIC TEXTURE & HORIZONTAL SPECULAR GRAIN (only high & ultra)
    if (this.quality === 'high' || this.quality === 'ultra') {
      ctx.save();
      for (let bg = 0; bg < 32; bg++) {
        const gy = floorTopY + (bg / 32) * floorH;
        const grainAlpha = (bg % 2 === 0 ? 0.035 : 0.018);
        ctx.fillStyle = `rgba(226, 232, 240, ${grainAlpha})`;
        ctx.fillRect(visibleLeft, gy, bgWidth, 1.2);
      }
      ctx.restore();
    }

    // 🌌 2B. WESTERN BASE SECTOR ILLUMINATION (Cold High-Tech Cyan Radial Light Dome)
    ctx.fillStyle = wBaseLight;
    ctx.fillRect(visibleLeft, floorTopY, Math.max(0, this.width * 0.58 - visibleLeft), floorH);

    // 🔥 2C. EASTERN BASE SECTOR ILLUMINATION (Warm Menace Crimson/Purple Radial Light Dome)
    ctx.fillStyle = eBaseLight;
    ctx.fillRect(this.width * 0.42, floorTopY, Math.max(0, visibleRight - this.width * 0.42), floorH);

    // 🌕 2D. FLICKERING CELESTIAL GOD RAYS & VOLUMETRIC MOONBEAMS (Gently undulating across floor)
    ctx.save();
    const beam1Alpha = (0.09 * moonFlicker);
    const moonBeam1 = ctx.createLinearGradient(1220, 0, 640 + moonSway, laneY + laneH);
    moonBeam1.addColorStop(0, `rgba(224, 242, 254, ${beam1Alpha * 1.2})`);
    moonBeam1.addColorStop(0.55, `rgba(186, 230, 253, ${beam1Alpha * 0.6})`);
    moonBeam1.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = moonBeam1;
    ctx.beginPath();
    ctx.moveTo(1160, floorTopY);
    ctx.lineTo(1300, floorTopY);
    ctx.lineTo(760 + moonSway, laneY + laneH);
    ctx.lineTo(560 + moonSway, laneY + laneH);
    ctx.closePath();
    ctx.fill();

    const beam2Alpha = (0.06 * moonFlicker);
    const moonBeam2 = ctx.createLinearGradient(1220, 0, 420 + moonSway * 0.7, laneY + laneH);
    moonBeam2.addColorStop(0, `rgba(224, 242, 254, ${beam2Alpha * 1.2})`);
    moonBeam2.addColorStop(0.55, `rgba(186, 230, 253, ${beam2Alpha * 0.5})`);
    moonBeam2.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = moonBeam2;
    ctx.beginPath();
    ctx.moveTo(1060, floorTopY);
    ctx.lineTo(1130, floorTopY);
    ctx.lineTo(470 + moonSway * 0.7, laneY + laneH);
    ctx.lineTo(380 + moonSway * 0.7, laneY + laneH);
    ctx.closePath();
    ctx.fill();

    // Soft Ambient Moon Wash across central floor
    const moonWash = ctx.createRadialGradient(850 + moonSway, floorTopY + floorH * 0.5, 50, 850 + moonSway, floorTopY + floorH * 0.5, 420);
    moonWash.addColorStop(0, `rgba(224, 242, 254, ${0.08 * moonFlicker})`);
    moonWash.addColorStop(0.6, `rgba(186, 230, 253, ${0.04 * moonFlicker})`);
    moonWash.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = moonWash;
    ctx.fillRect(400, floorTopY, 900, floorH);
    ctx.restore();

    // 🛡️ 2C. REINFORCED HEAVY SCI-FI INTERLOCKING ARMOR DECKING (With Metallic Bevels & Chrome Fasteners)
    const activeCraters = battlefield.radioactiveCraters;
    const hasCraters = activeCraters && activeCraters.length > 0;
    const plateRows = 9;
    const rowH = floorRowH;
    const colW = 90;
    const startCol = Math.floor(visibleLeft / colW) * colW;
    const endCol = Math.ceil(visibleRight / colW) * colW;

    for (let i = 0; i <= plateRows; i++) {
      const py = floorTopY + i * rowH;

      // Alternating modular plate row shading for distinct metallic panel differentiation
      if (i < plateRows) {
        ctx.fillStyle = (i % 2 === 0) ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.05)';
        ctx.fillRect(visibleLeft, py, bgWidth, rowH);
      }

      // 1. Dual-Tone Metallic Beveled Seams: Specular reflection lip + recessed shadow groove
      ctx.fillStyle = 'rgba(226, 232, 240, 0.22)'; // Crisp top-lit metallic edge glint
      ctx.fillRect(visibleLeft, py, bgWidth, 1.2);
      ctx.fillStyle = 'rgba(2, 6, 23, 0.85)';       // Deep recessed armor seam
      ctx.fillRect(visibleLeft, py + 1.2, bgWidth, 2.2);
      ctx.fillStyle = 'rgba(148, 163, 184, 0.12)'; // Lower bevel reflection
      ctx.fillRect(visibleLeft, py + 3.4, bgWidth, 0.8);

      // 2. Corner Hex-Bolts & Polished Chrome Rivets at plate intersections
      const rivetDepthT = (py - floorTopY) / floorH;
      for (let col = startCol; col <= endCol; col += colW) {
        const rivetX = col + (col - this.width / 2) * 0.34 * rivetDepthT;
        if (hasCraters) {
          let inCrater = false;
          for (const rc of activeCraters) {
            const crR = rc.radius * 0.40;
            if (Math.hypot(rivetX - rc.x, (py - rc.y) / 0.44) < crR * 0.95) {
              inCrater = true;
              break;
            }
          }
          if (inCrater) continue; // Excavated by nuke blast
        }
        // Heavy contact washer & socket
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(rivetX - 2.5, py - 2.5, 5, 5);
        // Machined steel washer
        ctx.fillStyle = '#475569';
        ctx.fillRect(rivetX - 2, py - 2, 4, 4);
        // Polished chrome rivet head
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(rivetX - 1, py - 1, 2, 2);
        // Specular white-hot glint
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(rivetX - 0.5, py - 1, 1, 1);
      }

      // 🌕 Moonlight Plate Sheen & pulsing energy data packets are both
      // time-varying (drift/flicker) - rendered live in
      // renderDynamicBackground(), which re-derives the same per-row `py`.
    }

    // 3. 3D Fanned Perspective Grid Lines with Metallic Plate Seams
    for (let x = startCol; x <= endCol; x += colW) {
      const topX = x;
      const bottomX = x + (x - this.width / 2) * 0.34;

      if (hasCraters) {
        let insideAny = false;
        for (const rc of activeCraters) {
          const crR = rc.radius * 0.40;
          if (Math.abs(topX - rc.x) < crR * 0.95) {
            insideAny = true;
            break;
          }
        }
        if (insideAny) {
          // Segmented line skipping crater void
          ctx.fillStyle = 'rgba(203, 213, 225, 0.14)';
          for (let segY = floorTopY; segY < laneY + laneH; segY += 8) {
            let inCrater = false;
            const curX = topX + (bottomX - topX) * ((segY - laneY) / laneH);
            for (const rc of activeCraters) {
              const crR = rc.radius * 0.40;
              if (Math.hypot(curX - rc.x, (segY - rc.y) / 0.44) < crR * 0.92) {
                inCrater = true;
                break;
              }
            }
            if (!inCrater) {
              ctx.fillRect(curX - 0.75, segY, 1.5, 6);
            }
          }
          continue;
        }
      }

      // Dual-tone metallic vertical seam: shadow edge + specular highlight line
      ctx.lineWidth = 2.0;
      ctx.strokeStyle = 'rgba(2, 6, 23, 0.65)';
      ctx.beginPath();
      ctx.moveTo(topX - 0.5, floorTopY);
      ctx.lineTo(bottomX - 0.5, laneY + laneH);
      ctx.stroke();

      ctx.lineWidth = 1.0;
      ctx.strokeStyle = 'rgba(203, 213, 225, 0.16)';
      ctx.beginPath();
      ctx.moveTo(topX + 0.5, floorTopY);
      ctx.lineTo(bottomX + 0.5, laneY + laneH);
      ctx.stroke();
    }

    // 🔧 2C-2. ARENA DECK IMPERFECTIONS & BATTLE-WORN DETAILS (5 subtle organic anomalies)
    // Breaks the mathematical perfection of the grid with realistic wear & tear:
    ctx.save();
    {
      // 1. Skid / Heavy Tread Scuff (West lane, x ≈ 310, y ≈ floorTopY + floorH * 0.58)
      const scuffX = 310, scuffY = floorTopY + floorH * 0.58;
      ctx.fillStyle = 'rgba(2, 6, 23, 0.45)';
      ctx.beginPath();
      ctx.ellipse(scuffX, scuffY, 26, 6, -0.28, 0, Math.PI * 2);
      ctx.fill();
      // Scratched bare-metal grooving
      ctx.strokeStyle = 'rgba(226, 232, 240, 0.16)';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(scuffX - 22, scuffY + 4);
      ctx.lineTo(scuffX + 18, scuffY - 5);
      ctx.moveTo(scuffX - 16, scuffY + 6);
      ctx.lineTo(scuffX + 24, scuffY - 3);
      ctx.stroke();

      // 2. Welded Repair Patch Plate (Mid-West, x ≈ 530, y ≈ floorTopY + floorH * 0.26)
      const patchX = 530, patchY = floorTopY + floorH * 0.26, patchW = 36, patchH = 15;
      // Darker replacement alloy panel
      ctx.fillStyle = 'rgba(15, 23, 42, 0.65)';
      ctx.fillRect(patchX - patchW / 2, patchY - patchH / 2, patchW, patchH);
      // Rough welded seam perimeter
      ctx.strokeStyle = 'rgba(71, 85, 105, 0.65)';
      ctx.lineWidth = 1.2;
      ctx.strokeRect(patchX - patchW / 2, patchY - patchH / 2, patchW, patchH);
      // Corner repair bolts
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(patchX - patchW / 2 + 2, patchY - patchH / 2 + 2, 2, 2);
      ctx.fillRect(patchX + patchW / 2 - 4, patchY - patchH / 2 + 2, 2, 2);
      ctx.fillRect(patchX - patchW / 2 + 2, patchY + patchH / 2 - 4, 2, 2);
      ctx.fillRect(patchX + patchW / 2 - 4, patchY + patchH / 2 - 4, 2, 2);

      // 3. Sub-Floor Ventilation / Service Grate with Diode (Center, x ≈ 725, y ≈ floorTopY + floorH * 0.74)
      const grateX = 725, grateY = floorTopY + floorH * 0.74, grateW = 42, grateH = 14;
      ctx.fillStyle = 'rgba(2, 6, 23, 0.85)';
      ctx.fillRect(grateX - grateW / 2, grateY - grateH / 2, grateW, grateH);
      // Slotted metal ribs
      ctx.fillStyle = 'rgba(100, 116, 139, 0.45)';
      for (let r = -grateW / 2 + 4; r < grateW / 2 - 4; r += 5) {
        ctx.fillRect(grateX + r, grateY - grateH / 2 + 2, 2, grateH - 4);
      }
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
      ctx.lineWidth = 1.0;
      ctx.strokeRect(grateX - grateW / 2, grateY - grateH / 2, grateW, grateH);
      // Subtle pulsing Amber service LED
      const ledPulse = 0.4 + 0.6 * Math.abs(Math.sin(t * 1.8));
      ctx.fillStyle = `rgba(245, 158, 11, ${0.45 * ledPulse})`;
      ctx.fillRect(grateX + grateW / 2 + 3, grateY - 1.5, 3, 3);

      // 4. Plasma Heat Tempering Bloom / Thermal Staining (Mid-East, x ≈ 975, y ≈ floorTopY + floorH * 0.44)
      const bloomX = 975, bloomY = floorTopY + floorH * 0.44;
      const bloomGrad = ctx.createRadialGradient(bloomX, bloomY, 4, bloomX, bloomY, 32);
      bloomGrad.addColorStop(0, 'rgba(147, 51, 234, 0.12)'); // Deep indigo/violet core
      bloomGrad.addColorStop(0.45, 'rgba(6, 182, 212, 0.09)'); // Cyan halo
      bloomGrad.addColorStop(0.8, 'rgba(245, 158, 11, 0.05)'); // Golden oxide edge
      bloomGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = bloomGrad;
      ctx.beginPath();
      ctx.ellipse(bloomX, bloomY, 34, 12, 0.18, 0, Math.PI * 2);
      ctx.fill();

      // 5. Structural Metal Micro-Stress Cracks (East lane, x ≈ 1235, y ≈ floorTopY + floorH * 0.62)
      const crackX = 1235, crackY = floorTopY + floorH * 0.62;
      ctx.strokeStyle = 'rgba(2, 6, 23, 0.88)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(crackX - 16, crackY + 3);
      ctx.lineTo(crackX - 7, crackY - 1);
      ctx.lineTo(crackX, crackY + 1);
      ctx.lineTo(crackX + 11, crackY - 3);
      ctx.lineTo(crackX + 18, crackY + 2);
      // Secondary hairline fork
      ctx.moveTo(crackX, crackY + 1);
      ctx.lineTo(crackX + 4, crackY + 6);
      ctx.stroke();
      // Catch-light on fractured lip
      ctx.strokeStyle = 'rgba(226, 232, 240, 0.18)';
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(crackX - 15, crackY + 4);
      ctx.lineTo(crackX - 6, crackY);
      ctx.lineTo(crackX + 1, crackY + 2);
      ctx.stroke();
    }
    ctx.restore();

    // ✨ 2D. SUBTLE OCCASIONAL METALLIC SPECULAR GLINTS (Random micro-flashes on steel plates)
    ctx.save();
    const glintCount = 22;
    for (let g = 0; g < glintCount; g++) {
      const gSeed = (g * 7919 + 31) % 997;
      const gxNorm = ((g * 137 + 43) % 1000) / 1000;
      const gyNorm = ((g * 229 + 17) % 1000) / 1000;

      const gx = visibleLeft + 40 + gxNorm * (bgWidth - 80);
      const gy = floorTopY + 12 + gyNorm * (floorH - 24);

      // Period: 6.5 to 11.0 seconds, brief 0.38s soft flash duration for a very subtle, organic feel
      const period = 6.5 + (gSeed % 7) * 0.75;
      const flashDuration = 0.38;
      const phase = (t + gSeed * 0.41) % period;

      if (phase < flashDuration) {
        const prog = phase / flashDuration;
        const glintAlpha = Math.sin(prog * Math.PI) * 0.65;

        // Skip if inside an active crater
        if (hasCraters) {
          let inCrater = false;
          for (const rc of activeCraters) {
            if (Math.hypot(gx - rc.x, (gy - rc.y) / 0.44) < rc.radius * 0.40) {
              inCrater = true;
              break;
            }
          }
          if (inCrater) continue;
        }

        // Central white-hot specular glint dot
        ctx.fillStyle = `rgba(255, 255, 255, ${glintAlpha})`;
        ctx.fillRect(gx - 0.5, gy - 0.5, 1.5, 1.5);

        // Subtle 4-point micro cross flare
        ctx.fillStyle = `rgba(224, 242, 254, ${glintAlpha * 0.55})`;
        ctx.fillRect(gx - 2.5, gy, 5, 0.75);
        ctx.fillRect(gx, gy - 2.5, 0.75, 5);
      }
    }
    ctx.restore();

    // 🌋 2E. COMPACT 3D SCULPTURAL NUCLEAR IMPACT CRATER & THERMAL CHASM
    // (Reduced by 60%, no harsh black patches, organic molten core with gradual smoke fade)
    if (hasCraters) {
      for (const rc of activeCraters) {
        if (rc.kind === 'glass') {
          // Sul Bio-Plasma Obsidian Glass Basin (docs/BIO_PLASMA_PLAN.md §7.5)
          ctx.save();
          const glR = rc.radius * 0.45;
          const glRy = glR * 0.44;

          // 1. Soft Violet-Charred Subsurface Halo
          const haloGrad = ctx.createRadialGradient(rc.x, rc.y, glR * 0.3, rc.x, rc.y, glR * 1.3);
          haloGrad.addColorStop(0, 'rgba(59, 7, 100, 0.55)');
          haloGrad.addColorStop(0.7, 'rgba(30, 27, 75, 0.25)');
          haloGrad.addColorStop(1, 'rgba(15, 23, 42, 0)');
          ctx.fillStyle = haloGrad;
          ctx.beginPath();
          ctx.ellipse(rc.x, rc.y, glR * 1.3, glRy * 1.3, 0, 0, Math.PI * 2);
          ctx.fill();

          // 2. Mirror-Smooth Obsidian Glass Base (Deep glossy black/violet)
          const glassGrad = ctx.createRadialGradient(rc.x, rc.y - 1, glR * 0.1, rc.x, rc.y, glR);
          glassGrad.addColorStop(0, '#020617');
          glassGrad.addColorStop(0.6, '#0f051d');
          glassGrad.addColorStop(0.92, '#1e1b4b');
          glassGrad.addColorStop(1, 'rgba(30, 27, 75, 0)');
          ctx.fillStyle = glassGrad;
          ctx.beginPath();
          ctx.ellipse(rc.x, rc.y, glR, glRy, 0, 0, Math.PI * 2);
          ctx.fill();

          // 3. Anisotropic Moonlight / Aurora Surface Reflection
          const specGrad = ctx.createLinearGradient(rc.x - glR * 0.7, rc.y - glRy, rc.x + glR * 0.7, rc.y + glRy);
          specGrad.addColorStop(0, 'rgba(217, 70, 239, 0)');
          specGrad.addColorStop(0.45, 'rgba(253, 244, 255, 0.28)');
          specGrad.addColorStop(0.55, 'rgba(192, 132, 252, 0.32)');
          specGrad.addColorStop(1, 'rgba(217, 70, 239, 0)');
          ctx.fillStyle = specGrad;
          ctx.beginPath();
          ctx.ellipse(rc.x, rc.y, glR * 0.88, glRy * 0.85, -0.15, 0, Math.PI * 2);
          ctx.fill();

          // Glass Edge Highlights
          ctx.strokeStyle = 'rgba(217, 70, 239, 0.45)';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.ellipse(rc.x, rc.y, glR * 0.96, glRy * 0.94, 0, 0, Math.PI * 2);
          ctx.stroke();

          // 4. Creeping Bio-Green Nebular Mists rising from obsidian glass
          for (let m = 0; m < 4; m++) {
            const mistProg = (t * (0.4 + m * 0.12) + m * 0.25) % 1.0;
            const mistX = rc.x + Math.sin(t * 1.5 + m * 2.1) * (glR * 0.55);
            const mistY = rc.y - mistProg * 18 + (m * 3 - 6);
            const mistAlpha = Math.sin(mistProg * Math.PI) * 0.35;
            const mistR = 12 + mistProg * 14;

            const mistGrad = ctx.createRadialGradient(mistX, mistY, 2, mistX, mistY, mistR);
            mistGrad.addColorStop(0, `rgba(132, 204, 22, ${mistAlpha})`);
            mistGrad.addColorStop(0.5, `rgba(163, 230, 53, ${mistAlpha * 0.5})`);
            mistGrad.addColorStop(1, 'rgba(132, 204, 22, 0)');
            ctx.fillStyle = mistGrad;
            ctx.beginPath();
            ctx.ellipse(mistX, mistY, mistR, mistR * 0.45, 0, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.restore();
          continue;
        }

        ctx.save();
        const crR = rc.radius * 0.40; // 60% size reduction (48px radius)
        const crRy = crR * 0.44;     // 2.5D perspective ellipse factor (~21px)
        const magmaPulse = 0.75 + 0.25 * Math.sin(t * 2.4 + rc.x * 0.02);

        // 1. SOFT SCORCHED HEAT HALO (Blends smoothly into decking, no harsh black blobs)
        const scorchGrad = ctx.createRadialGradient(rc.x, rc.y, crR * 0.4, rc.x, rc.y, crR * 1.35);
        scorchGrad.addColorStop(0, 'rgba(15, 23, 42, 0.65)');
        scorchGrad.addColorStop(0.65, 'rgba(30, 41, 59, 0.35)');
        scorchGrad.addColorStop(1, 'rgba(15, 23, 42, 0)');
        ctx.fillStyle = scorchGrad;
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y, crR * 1.35, crRy * 1.35, 0, 0, Math.PI * 2);
        ctx.fill();

        // 2. SUNKEN 3D CAVITY BASIN & SUB-DECK STRATA
        // Basin Bowl Slope
        const basinGrad = ctx.createRadialGradient(rc.x, rc.y - 2, crR * 0.1, rc.x, rc.y, crR);
        basinGrad.addColorStop(0, '#0a0f1d'); // Deep foundation
        basinGrad.addColorStop(0.7, '#111827'); // Chiseled basalt slope
        basinGrad.addColorStop(0.92, '#1e293b'); // Charred metal lip
        basinGrad.addColorStop(1, 'rgba(30, 41, 59, 0)');
        ctx.fillStyle = basinGrad;
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y, crR, crRy, 0, 0, Math.PI * 2);
        ctx.fill();

        // 3D Rim Chiseled Edge Catch: Upper rim catches moonlight highlight
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y - 1.5, crR * 0.98, crRy * 0.95, -0.15, -Math.PI * 0.85, Math.PI * 0.15);
        ctx.stroke();

        ctx.strokeStyle = '#64748b';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y - 2, crR * 0.96, crRy * 0.93, -0.15, -Math.PI * 0.65, -0.05);
        ctx.stroke();

        // Soft Inner Wall Shadow (Upper rim shadow)
        ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y - 3, crR * 0.85, crRy * 0.52, 0, Math.PI, Math.PI * 2);
        ctx.fill();

        // 3. SEARING MOLTEN MAGMA CORE (Organic glow, NO central spoked lines!)
        const coreMagma = ctx.createRadialGradient(rc.x, rc.y + 2, 1, rc.x, rc.y + 2, crR * 0.55);
        coreMagma.addColorStop(0, `rgba(255, 255, 255, ${0.98 * magmaPulse})`); // White-hot core
        coreMagma.addColorStop(0.22, `rgba(254, 240, 138, ${0.92 * magmaPulse})`); // Incandescent gold
        coreMagma.addColorStop(0.55, `rgba(249, 115, 22, ${0.75 * magmaPulse})`); // Volcanic orange
        coreMagma.addColorStop(0.85, `rgba(185, 28, 28, ${0.35 * magmaPulse})`);  // Crimson perimeter
        coreMagma.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = coreMagma;
        ctx.beginPath();
        ctx.ellipse(rc.x, rc.y + 2, crR * 0.55, crRy * 0.52, 0, 0, Math.PI * 2);
        ctx.fill();

        // Subtle Organic Thermal Edge Hotspots (non-crossing, asymmetric)
        const hotSpots = [
          { ox: -crR * 0.22, oy: -crRy * 0.12, r: crR * 0.15, col: '#fef08a' },
          { ox: crR * 0.25, oy: crRy * 0.15, r: crR * 0.18, col: '#f97316' },
          { ox: crR * 0.05, oy: -crRy * 0.2, r: crR * 0.12, col: '#fde047' }
        ];
        for (const hs of hotSpots) {
          ctx.fillStyle = hs.col;
          ctx.globalAlpha = 0.65 * magmaPulse;
          ctx.beginPath();
          ctx.ellipse(rc.x + hs.ox, rc.y + 2 + hs.oy, hs.r, hs.r * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // 4. SUBTLE TORN RIM SHARDS (Small proportional metal teeth around edge)
        const plateCount = 6;
        for (let p = 0; p < plateCount; p++) {
          const pAng = p * (Math.PI * 2 / plateCount) + 0.35;
          const px = rc.x + Math.cos(pAng) * (crR * 0.95);
          const py = rc.y + Math.sin(pAng) * (crRy * 0.95);

          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(pAng + Math.PI / 2);

          // Plate drop shadow
          ctx.fillStyle = 'rgba(2, 6, 23, 0.6)';
          ctx.fillRect(-3, 1, 6, 2.5);

          // Steel tooth
          ctx.fillStyle = p % 2 === 0 ? '#334155' : '#1e293b';
          ctx.fillRect(-2.5, -3.5, 5, 4);

          // Top glint
          ctx.fillStyle = '#94a3b8';
          ctx.fillRect(-2.5, -3.5, 5, 1);

          // Underside warm thermal glow
          ctx.fillStyle = 'rgba(249, 115, 22, 0.7)';
          ctx.fillRect(-2, 0, 4, 1);

          ctx.restore();
        }

        // 5. GRADUAL VOLUMETRIC SMOKE DISSIPATION & FLOATING EMBERS (Graduelles Abklingen)
        for (let m = 0; m < 5; m++) {
          // Floating Molten Ember Spark
          const sparkProg = (t * (0.7 + m * 0.2) + m * 0.22) % 1.0;
          const ex = rc.x + Math.sin(t * 2.0 + m * 1.5) * (crR * 0.3);
          const ey = (rc.y + 2) - sparkProg * 42;
          const eAlpha = (1.0 - sparkProg) * 0.85 * magmaPulse;
          ctx.fillStyle = m % 2 === 0 ? '#fef08a' : '#f97316';
          ctx.globalAlpha = eAlpha;
          ctx.fillRect(ex - 1, ey - 1, 2, 2);

          // Gradual Soft Smoke Puff with cubic fade
          const smokeProg = (t * 0.45 + m * 0.2) % 1.0;
          const smX = rc.x + Math.sin(t * 1.0 + m) * (crR * 0.35) + (smokeProg * 14);
          const smY = (rc.y + 2) - smokeProg * 55;
          const smFade = Math.pow(1.0 - smokeProg, 1.5);
          const smRadius = 3 + smokeProg * 9;

          const smokeGrad = ctx.createRadialGradient(smX, smY, 1, smX, smY, smRadius);
          smokeGrad.addColorStop(0, `rgba(71, 85, 105, ${0.35 * smFade})`);
          smokeGrad.addColorStop(0.6, `rgba(100, 116, 139, ${0.18 * smFade})`);
          smokeGrad.addColorStop(1, 'rgba(148, 163, 184, 0)');
          ctx.fillStyle = smokeGrad;
          ctx.globalAlpha = 1.0;
          ctx.beginPath();
          ctx.arc(smX, smY, smRadius, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    }

    // ✨ Ambient Bioluminescent Energy Spores & Fireflies and the 🔦 sweeping
    // base searchlight cones are both fully time-driven (no static "parked"
    // pose worth keeping) - rendered live in renderDynamicBackground().

    // Upper 3D Retaining Curb & Drop Shadow
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(visibleLeft, laneY - 6, bgWidth, 6);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(visibleLeft, laneY, bgWidth, 12);

    // Lower 3D Barrier Wall with Hazard Stripes & Steam Exhaust Vents
    const lowerY = laneY + laneH;
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(visibleLeft, lowerY, bgWidth, 16);
    ctx.fillStyle = '#334155';
    ctx.fillRect(visibleLeft, lowerY, bgWidth, 3);
    for (let x = visibleLeft; x < visibleRight; x += 36) {
      ctx.fillStyle = '#facc15';
      ctx.fillRect(x, lowerY + 4, 12, 8);
    }

    // Exhaust Vent Housings (2 outer locations; the rising steam puff & heat
    // mirage shimmer above them are time-varying - rendered live in
    // renderDynamicBackground())
    const ventPositions = [320, 1280];
    for (const vx of ventPositions) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(vx, lowerY + 2, 24, 10);
    }

    // 🚨 RED HAZARD BEACON FIXTURES (Physical mounts baked statically; light sweeps rendered dynamically in renderDynamicBackground)
    {
      const midX = this.width / 2; // 800px

      // Upper Beacon Fixture (Small background scale ~0.65x)
      const topBx = midX;
      const topBy = laneY - 4; // 172px

      ctx.save();
      // Base bracket
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(topBx - 5, topBy - 1, 10, 4);
      ctx.fillStyle = '#475569';
      ctx.fillRect(topBx - 4, topBy - 1, 8, 1.5);
      // Dome glass
      ctx.fillStyle = '#7f1d1d';
      ctx.fillRect(topBx - 3, topBy - 7, 6, 6);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(topBx - 2.5, topBy - 6.5, 5, 5);
      // Top cap
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(topBx - 3, topBy - 8, 6, 1.5);
      ctx.restore();

      // Lower Beacon Fixture (Large foreground scale ~1.5x)
      const botBx = midX;
      const botBy = lowerY + 2; // 498px

      ctx.save();
      // Heavy base bracket with contact shadow
      ctx.fillStyle = 'rgba(2, 6, 23, 0.75)';
      ctx.beginPath();
      ctx.ellipse(botBx, botBy + 8, 14, 5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(botBx - 11, botBy - 2, 22, 9);
      ctx.fillStyle = '#334155';
      ctx.fillRect(botBx - 10, botBy - 2, 20, 3);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(botBx - 10, botBy - 2, 20, 1);

      // 4 Hex Anchor Bolts on foreground mount
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(botBx - 8, botBy + 2, 2.5, 2.5);
      ctx.fillRect(botBx + 5.5, botBy + 2, 2.5, 2.5);

      // Red Glass Dome (12x14px)
      ctx.fillStyle = '#7f1d1d';
      ctx.fillRect(botBx - 7, botBy - 15, 14, 13);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(botBx - 6, botBy - 14, 12, 11);
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(botBx - 4.5, botBy - 13, 9, 9);

      // Top cap & mounting ring
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(botBx - 7, botBy - 17, 14, 3);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(botBx - 6, botBy - 17, 12, 1);

      // Vertical Protective Wire Cage Mullions
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(botBx - 6, botBy - 15, 1.5, 13);
      ctx.fillRect(botBx - 0.75, botBy - 15, 1.5, 13);
      ctx.fillRect(botBx + 4.5, botBy - 15, 1.5, 13);
      ctx.restore();
    }

    // 🏟️ BOLD INDUSTRIAL STADIUM CORNER FLOODLIGHT TOWERS & BASE ILLUMINATION
    // Upper-left: distant background lattice tower aiming DOWN-RIGHT at Western Base (x:120, y:320)
    // Lower-right: massive foreground gantry tower (scaled 1.6x) aiming UP-LEFT at Eastern Base (x:1480, y:320)
    {
      // ─────────────────────────────────────────────────────────────────
      // 1. UPPER-LEFT STADIUM FLOODLIGHT TOWER (Background, Aiming at Western Base)
      // ─────────────────────────────────────────────────────────────────
      const ulMastX = 42;
      const ulBaseY = laneY + 2;   // 178px
      const ulTopY = 48;           // 130px tall gantry in distant background
      const ulHeadX = 64;
      const ulHeadY = 52;
      // (ulTargetX/ulTargetY - Western Base aim point - now only needed by
      // the dynamic beam in renderDynamicBackground())

      // ── Soft Atmospheric Volumetric Light Shaft (Upper-Left -> Western Base) ──
      // Beam/core/pool/dust-motes are all time-varying (floodPulse, drifting
      // motes) - rendered live in renderDynamicBackground() (Paradigma 7).

      // ── Physical Upper-Left Lattice Gantry Tower (Distant Background) ──
      ctx.save();
      // Foundation footing
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(ulMastX - 14, ulBaseY - 8, 28, 10);
      ctx.fillStyle = '#334155';
      ctx.fillRect(ulMastX - 12, ulBaseY - 8, 24, 3);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(ulMastX - 12, ulBaseY - 8, 24, 1);
      ctx.fillStyle = '#fde047';
      ctx.fillRect(ulMastX - 9, ulBaseY - 5, 2.5, 2.5);
      ctx.fillRect(ulMastX + 6.5, ulBaseY - 5, 2.5, 2.5);

      const mastWTop = 13;
      const mastWBot = 20;
      const mastH = ulBaseY - ulTopY;

      // Leg 1 (Left)
      ctx.fillStyle = '#1e293b';
      ctx.beginPath();
      ctx.moveTo(ulMastX - mastWTop / 2, ulTopY);
      ctx.lineTo(ulMastX - mastWBot / 2, ulBaseY);
      ctx.lineTo(ulMastX - mastWBot / 2 + 2.5, ulBaseY);
      ctx.lineTo(ulMastX - mastWTop / 2 + 2.5, ulTopY);
      ctx.closePath();
      ctx.fill();

      // Leg 2 (Right)
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.moveTo(ulMastX + mastWTop / 2 - 2.5, ulTopY);
      ctx.lineTo(ulMastX + mastWBot / 2 - 2.5, ulBaseY);
      ctx.lineTo(ulMastX + mastWBot / 2, ulBaseY);
      ctx.lineTo(ulMastX + mastWTop / 2, ulTopY);
      ctx.closePath();
      ctx.fill();

      // Specular edge highlights
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(ulMastX - mastWTop / 2, ulTopY, 1.2, mastH);
      ctx.fillRect(ulMastX + mastWTop / 2 - 1.2, ulTopY, 1.2, mastH);

      // Steel Cross-Bracing
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let gy = ulTopY + 12; gy < ulBaseY - 8; gy += 18) {
        const t1 = (gy - ulTopY) / mastH;
        const t2 = (gy + 18 - ulTopY) / mastH;
        const w1 = mastWTop + (mastWBot - mastWTop) * t1;
        const w2 = mastWTop + (mastWBot - mastWTop) * t2;
        ctx.moveTo(ulMastX - w1 / 2, gy);
        ctx.lineTo(ulMastX + w1 / 2, gy);
        ctx.moveTo(ulMastX - w1 / 2, gy);
        ctx.lineTo(ulMastX + w2 / 2, gy + 18);
        ctx.moveTo(ulMastX + w1 / 2, gy);
        ctx.lineTo(ulMastX - w2 / 2, gy + 18);
      }
      ctx.stroke();

      // Service Ladder
      ctx.fillStyle = '#facc15';
      for (let ly = ulTopY + 8; ly < ulBaseY - 8; ly += 6) {
        ctx.fillRect(ulMastX - 2, ly, 4, 1.2);
      }

      // Catwalk Platform
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(ulMastX - 14, ulTopY - 2, 32, 5);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(ulMastX - 14, ulTopY - 2, 32, 1.5);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1.0;
      ctx.strokeRect(ulMastX - 14, ulTopY - 9, 32, 7);

      // Outrigger Arm
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(ulMastX + 5, ulTopY - 3, 14, 6);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(ulMastX + 5, ulTopY - 3, 14, 1.5);

      // Floodlight Head (Angled ~45° Down-Right)
      ctx.save();
      ctx.translate(ulHeadX, ulHeadY);
      ctx.rotate(0.78);

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(-13, -8, 26, 16);
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(-11, -6, 22, 12);
      ctx.fillStyle = '#0284c7';
      ctx.fillRect(-13, -8, 26, 1.5);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(-13, -8, 26, 0.8);

      // 6 Projector Bulbs
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 3; col++) {
          const bx = -8 + col * 8;
          const by = -4 + row * 7;
          ctx.fillStyle = '#334155';
          ctx.fillRect(bx - 3, by - 2.5, 6, 5);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(bx - 1.2, by - 1.2, 2.4, 2.4);
          ctx.fillStyle = '#38bdf8';
          ctx.fillRect(bx - 0.5, by - 0.5, 1, 1);
        }
      }

      // Head Flare & Anamorphic Lens Flare + the rotating hazard beacon are
      // time-varying (floodPulse / blink) - rendered live in renderDynamicBackground().
      ctx.restore();
      ctx.restore();


      // ─────────────────────────────────────────────────────────────────
      // 2. UPPER-RIGHT STADIUM FLOODLIGHT TOWER (Symmetrical Counterpart to Upper-Left, Aiming at Eastern Base)
      // ─────────────────────────────────────────────────────────────────
      const urMastX = 1558;
      const urBaseY = laneY + 2;   // 178px
      const urTopY = 48;           // 130px tall gantry in distant background (matches upper-left)
      const urHeadX = 1536;
      const urHeadY = 52;
      // (urTargetX/urTargetY - Eastern Base aim point - now only needed by
      // the dynamic beam in renderDynamicBackground())

      // ── Soft Atmospheric Volumetric Light Shaft (Upper-Right -> Eastern Base) ──
      // Beam/core/pool/dust-motes are all time-varying (floodPulse, drifting
      // motes) - rendered live in renderDynamicBackground() (Paradigma 7).

      // ── Physical Upper-Right Lattice Gantry Tower (Distant Background) ──
      ctx.save();
      // Foundation footing
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(urMastX - 14, urBaseY - 8, 28, 10);
      ctx.fillStyle = '#334155';
      ctx.fillRect(urMastX - 12, urBaseY - 8, 24, 3);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(urMastX - 12, urBaseY - 8, 24, 1);
      ctx.fillStyle = '#fde047';
      ctx.fillRect(urMastX - 9, urBaseY - 5, 2.5, 2.5);
      ctx.fillRect(urMastX + 6.5, urBaseY - 5, 2.5, 2.5);

      // Leg 1 (Left Outer)
      ctx.fillStyle = '#1e293b';
      ctx.beginPath();
      ctx.moveTo(urMastX - mastWTop / 2, urTopY);
      ctx.lineTo(urMastX - mastWBot / 2, urBaseY);
      ctx.lineTo(urMastX - mastWBot / 2 + 2.5, urBaseY);
      ctx.lineTo(urMastX - mastWTop / 2 + 2.5, urTopY);
      ctx.closePath();
      ctx.fill();

      // Leg 2 (Right Outer)
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.moveTo(urMastX + mastWTop / 2 - 2.5, urTopY);
      ctx.lineTo(urMastX + mastWBot / 2 - 2.5, urBaseY);
      ctx.lineTo(urMastX + mastWBot / 2, urBaseY);
      ctx.lineTo(urMastX + mastWTop / 2, urTopY);
      ctx.closePath();
      ctx.fill();

      // Specular edge highlights
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(urMastX - mastWTop / 2, urTopY, 1.2, mastH);
      ctx.fillRect(urMastX + mastWTop / 2 - 1.2, urTopY, 1.2, mastH);

      // Steel Cross-Bracing
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let gy = urTopY + 12; gy < urBaseY - 8; gy += 18) {
        const t1 = (gy - urTopY) / mastH;
        const t2 = (gy + 18 - urTopY) / mastH;
        const w1 = mastWTop + (mastWBot - mastWTop) * t1;
        const w2 = mastWTop + (mastWBot - mastWTop) * t2;
        ctx.moveTo(urMastX - w1 / 2, gy);
        ctx.lineTo(urMastX + w1 / 2, gy);
        ctx.moveTo(urMastX - w1 / 2, gy);
        ctx.lineTo(urMastX + w2 / 2, gy + 18);
        ctx.moveTo(urMastX + w1 / 2, gy);
        ctx.lineTo(urMastX - w2 / 2, gy + 18);
      }
      ctx.stroke();

      // Service Ladder
      ctx.fillStyle = '#facc15';
      for (let ly = urTopY + 8; ly < urBaseY - 8; ly += 6) {
        ctx.fillRect(urMastX - 2, ly, 4, 1.2);
      }

      // Catwalk Platform
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(urMastX - 18, urTopY - 2, 32, 5);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(urMastX - 18, urTopY - 2, 32, 1.5);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1.0;
      ctx.strokeRect(urMastX - 18, urTopY - 9, 32, 7);

      // Outrigger Arm (Points left towards head)
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(urMastX - 19, urTopY - 3, 14, 6);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(urMastX - 19, urTopY - 3, 14, 1.5);

      // Floodlight Head (Angled ~-45° Down-Left toward Eastern Base)
      ctx.save();
      ctx.translate(urHeadX, urHeadY);
      ctx.rotate(-0.78); // -45 deg downward-left

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(-13, -8, 26, 16);
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(-11, -6, 22, 12);
      ctx.fillStyle = '#b91c1c'; // Dominion crimson accent trim
      ctx.fillRect(-13, -8, 26, 1.5);
      ctx.fillStyle = '#fde047'; // Brass specular highlight
      ctx.fillRect(-13, -8, 26, 0.8);

      // 6 Projector Bulbs
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 3; col++) {
          const bx = -8 + col * 8;
          const by = -4 + row * 7;
          ctx.fillStyle = '#334155';
          ctx.fillRect(bx - 3, by - 2.5, 6, 5);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(bx - 1.2, by - 1.2, 2.4, 2.4);
          ctx.fillStyle = '#fde047'; // Warm amber lens glint
          ctx.fillRect(bx - 0.5, by - 0.5, 1, 1);
        }
      }

      // Head Flare & Anamorphic Lens Flare + the rotating hazard beacon are
      // time-varying (floodPulse / blink) - rendered live in renderDynamicBackground().
      ctx.restore();
      ctx.restore();
    }
  },

  // 3. 🌋 SCULPTURAL 3D RELIEF FOREGROUND WASTELAND & CHISELED BEDROCK STRATA
  // Rendered in front of the battlefield floor and territory indicator rail so
  // foreground cliff facets, pylons, and mech crawler props naturally occlude the floor.
  renderStaticForeground(ctx, battlefield, customBounds = null) {
    if (!battlefield) return;
    const t = 0;
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const lowerY = laneY + laneH;

    // Visible bounds from camera or custom bounds to eliminate black bars on any aspect ratio / screen size
    const bounds = customBounds || (this.viewport ? this.viewport.getVisibleBounds() : null);
    const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
    const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (this.height + 40))) : (this.height + 40);
    const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
    const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (this.width + 40))) : (this.width + 40);

    const visibleTop = customBounds ? rawTop : Math.min(0, rawTop - 20);
    const visibleBottom = customBounds ? rawBottom : Math.max(this.height, rawBottom + 20);
    const visibleLeft = customBounds ? rawLeft : Math.min(0, rawLeft - 20);
    const visibleRight = customBounds ? rawRight : Math.max(this.width, rawRight + 20);
    const bgWidth = visibleRight - visibleLeft;

    const fgTop = lowerY + 16;
    const fgH = visibleBottom - fgTop;

    // 1. Deep Abyssal Geological Soil & Bedrock Gradient (Enriched with warm volcanic umber, indigo & mineral tones)
    const fgGrad = ctx.createLinearGradient(0, fgTop, 0, visibleBottom);
    fgGrad.addColorStop(0, '#1e1b2e');      // Deep mineral indigo
    fgGrad.addColorStop(0.16, '#2a1a16');   // Warm volcanic umber / terracotta
    fgGrad.addColorStop(0.38, '#1e1628');   // Rich amethyst basalt
    fgGrad.addColorStop(0.65, '#121422');   // Deep cobalt slate
    fgGrad.addColorStop(1, '#050408');      // Abyssal bedrock
    ctx.fillStyle = fgGrad;
    ctx.fillRect(visibleLeft, fgTop, bgWidth, fgH);

    // 2. Weathered Security Perimeter Curb with 3D Bevel, Copper Seams & Under-Shadow
    ctx.fillStyle = '#262d3d';
    ctx.fillRect(visibleLeft, fgTop, bgWidth, 6);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(visibleLeft, fgTop, bgWidth, 2); // Top rim highlight
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(visibleLeft, fgTop, bgWidth, 0.75); // Specular edge line
    ctx.fillStyle = '#020617';
    ctx.fillRect(visibleLeft, fgTop + 6, bgWidth, 5); // Deep ambient shadow

    // 2 Perimeter Anchor Blocks at outer bases with copper fittings & rust oxidation
    const baseStanchions = [90, 1510];
    for (const stX of baseStanchions) {
      ctx.fillStyle = '#020617';
      ctx.fillRect(stX - 4, fgTop - 1, 10, 8);
      ctx.fillStyle = '#334155';
      ctx.fillRect(stX - 3, fgTop, 8, 6);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(stX - 3, fgTop, 8, 2);
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(stX - 1, fgTop + 2, 2, 2); // Polished brass bolt
      ctx.fillStyle = '#b45309';
      ctx.fillRect(stX - 2, fgTop + 6, 5, 4); // Copper oxide runoff
      ctx.fillStyle = '#d97706';
      ctx.fillRect(stX - 1, fgTop + 7, 3, 2);
    }

    // 3. TIER 1 CHISELED ROCK SHELF (Upper Terraced Cliff Edge with 3D Facets & Mineral Veins)
    ctx.fillStyle = '#141824';
    ctx.beginPath();
    ctx.moveTo(visibleLeft, fgTop + 14);
    ctx.lineTo(160, fgTop + 11);
    ctx.lineTo(340, fgTop + 18);
    ctx.lineTo(520, fgTop + 12);
    ctx.lineTo(720, fgTop + 20);
    ctx.lineTo(940, fgTop + 13);
    ctx.lineTo(1180, fgTop + 19);
    ctx.lineTo(1380, fgTop + 12);
    ctx.lineTo(visibleRight, fgTop + 16);
    ctx.lineTo(visibleRight, visibleBottom);
    ctx.lineTo(visibleLeft, visibleBottom);
    ctx.closePath();
    ctx.fill();

    // Tier 1 Illuminated Top Lip Bevel (with warm amber & slate highlights)
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(visibleLeft, fgTop + 14);
    ctx.lineTo(160, fgTop + 11);
    ctx.lineTo(340, fgTop + 18);
    ctx.lineTo(520, fgTop + 12);
    ctx.lineTo(720, fgTop + 20);
    ctx.lineTo(940, fgTop + 13);
    ctx.lineTo(1180, fgTop + 19);
    ctx.lineTo(1380, fgTop + 12);
    ctx.lineTo(visibleRight, fgTop + 16);
    ctx.stroke();

    // Fast-path early exit for Low quality: basic cliff and curb are rendered cleanly,
    // skipping ~900 lines of complex procedural micro-vignettes every frame.
    if (this.quality === 'low') return;

    // Emerald & Cyan Bioluminescent Lichen creeping along Tier 1 rock rim
    const mossPatchesT1 = [
      { x: 120, w: 26, col: '#059669', dot: '#34d399' },
      { x: 480, w: 32, col: '#047857', dot: '#10b981' },
      { x: 760, w: 22, col: '#0891b2', dot: '#67e8f9' },
      { x: 1120, w: 28, col: '#059669', dot: '#34d399' },
      { x: 1420, w: 24, col: '#047857', dot: '#10b981' }
    ];
    for (const mp of mossPatchesT1) {
      ctx.fillStyle = mp.col;
      ctx.fillRect(mp.x, fgTop + 12, mp.w, 2);
      ctx.fillStyle = mp.dot;
      ctx.fillRect(mp.x + 4, fgTop + 11.5, mp.w * 0.5, 1);
      ctx.fillRect(mp.x + 8, fgTop + 13.5, 3, 1.5);
    }

    // Specular Catch Glints & Warm Copper Flares on sharp Tier 1 rock corners
    const glintPeaks = [160, 520, 940, 1380];
    for (const gx of glintPeaks) {
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(gx - 12, fgTop + 10, 24, 1.5);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(gx - 4, fgTop + 10, 8, 1.5);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(gx - 2, fgTop + 11.5, 4, 1); // Warm copper sun glint
    }

    // 4. TIER 2 STEPPED BASALT SHELF & ANGULAR PROTRUDING BUTTRESSES (Mid-Level 3D Relief)
    ctx.fillStyle = '#0a0d16';
    ctx.beginPath();
    ctx.moveTo(visibleLeft, fgTop + 28);
    ctx.lineTo(220, fgTop + 23);
    ctx.lineTo(440, fgTop + 32);
    ctx.lineTo(680, fgTop + 25);
    ctx.lineTo(890, fgTop + 34);
    ctx.lineTo(1120, fgTop + 24);
    ctx.lineTo(1340, fgTop + 31);
    ctx.lineTo(visibleRight, fgTop + 27);
    ctx.lineTo(visibleRight, visibleBottom);
    ctx.lineTo(visibleLeft, visibleBottom);
    ctx.closePath();
    ctx.fill();

    // Tier 2 Top Bevel & Chiseled Facet Edges
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(visibleLeft, fgTop + 28);
    ctx.lineTo(220, fgTop + 23);
    ctx.lineTo(440, fgTop + 32);
    ctx.lineTo(680, fgTop + 25);
    ctx.lineTo(890, fgTop + 34);
    ctx.lineTo(1120, fgTop + 24);
    ctx.lineTo(1340, fgTop + 31);
    ctx.lineTo(visibleRight, fgTop + 27);
    ctx.stroke();

    // Horizontal Sedimentary Strata Bands with 3D Light/Shadow Bevel Pairs & Rich Mineral Colors
    const strataData = [
      { y: 38, shadow: 'rgba(2, 6, 23, 0.95)', ridge: '#78350f', highlight: '#d97706' }, // Warm copper/iron vein
      { y: 52, shadow: 'rgba(2, 6, 23, 0.9)', ridge: '#1e1b4b', highlight: '#6366f1' },  // Lapis / amethyst stratum
      { y: 68, shadow: 'rgba(2, 6, 23, 0.9)', ridge: '#14532d', highlight: '#10b981' }   // Deep jade mineral seam
    ];
    for (const st of strataData) {
      if (fgTop + st.y < visibleBottom) {
        // Deep shadow crease
        ctx.fillStyle = st.shadow;
        ctx.fillRect(visibleLeft, fgTop + st.y, bgWidth, 2.5);
        // Colored mineral ridge band
        ctx.fillStyle = st.ridge;
        ctx.fillRect(visibleLeft, fgTop + st.y + 2, bgWidth, 1.5);
        // Subtle illuminated gemstone vein sparkles
        ctx.fillStyle = st.highlight;
        ctx.globalAlpha = 0.45;
        ctx.fillRect(visibleLeft, fgTop + st.y + 2.5, bgWidth, 0.75);
        ctx.globalAlpha = 1.0;
      }
    }

    // 5. Chiseled Natural Crevasse Fault with Deep Shadow Depth (x: ~580-680)
    ctx.strokeStyle = '#020617';
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.moveTo(580, fgTop + 14);
    ctx.lineTo(625, fgTop + 32);
    ctx.lineTo(680, fgTop + 48);
    ctx.stroke();

    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(582, fgTop + 14);
    ctx.lineTo(627, fgTop + 32);
    ctx.lineTo(682, fgTop + 48);
    ctx.stroke();

    // Glowing Cyan Crystal Micro-Cluster inside Crevasse
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(626, fgTop + 30, 2.5, 3.5);
    ctx.fillStyle = '#e0f2fe';
    ctx.fillRect(627, fgTop + 30, 1, 2);

    // =========================================================================
    // 🌿 4. DELIBERATE FOCAL VIGNETTES & HIGH-QUALITY SCI-FI FOCAL OBJECTS
    // =========================================================================

    // --- VIGNETTE 1: Western Basalt Grotto (x: ~220-270) ---
    const v1X = 240;
    const v1Y = fgTop + 26;

    // Rock Ledge Base
    ctx.fillStyle = 'rgba(2, 6, 23, 0.7)';
    ctx.fillRect(v1X - 28, v1Y + 8, 62, 5);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(v1X - 25, v1Y - 4, 56, 12);
    ctx.fillStyle = '#475569';
    ctx.fillRect(v1X - 23, v1Y - 4, 52, 2.5);

    // Glowing Azure Crystal Geode
    const pulse1 = 0.65 + 0.35 * Math.sin(t * 2.8);
    const cg1 = ctx.createRadialGradient(v1X - 12, v1Y - 2, 1, v1X - 12, v1Y - 2, 16);
    cg1.addColorStop(0, 'rgba(0, 240, 255, 0.32)');
    cg1.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = cg1;
    ctx.fillRect(v1X - 26, v1Y - 14, 28, 24);

    // Crystal Shards
    ctx.fillStyle = '#00f0ff';
    ctx.beginPath();
    ctx.moveTo(v1X - 15, v1Y + 4);
    ctx.lineTo(v1X - 12, v1Y - 8);
    ctx.lineTo(v1X - 9, v1Y + 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#e0f2fe';
    ctx.globalAlpha = pulse1;
    ctx.fillRect(v1X - 13, v1Y - 7, 2, 4);
    ctx.globalAlpha = 1.0;

    // 2 Bioluminescent Parasol Shrooms beside the crystal
    const shrooms1 = [
      { ox: 6, oy: -2, r: 6, h: 10, col: '#06b6d4', cap: '#67e8f9' },
      { ox: 18, oy: 1, r: 8, h: 14, col: '#0891b2', cap: '#a5f3fc' }
    ];
    for (const sh of shrooms1) {
      const sx = v1X + sh.ox;
      const sy = v1Y + sh.oy;
      const sPulse = 0.7 + 0.3 * Math.sin(t * 3.2 + sx);

      // Moss patch
      ctx.fillStyle = '#064e3b';
      ctx.fillRect(sx - sh.r - 1, sy + sh.h - 3, sh.r * 2 + 2, 3);
      // Stalk
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx - 1.5, sy, 3, sh.h);
      // Cap
      ctx.fillStyle = sh.col;
      ctx.beginPath();
      ctx.ellipse(sx, sy, sh.r, sh.r * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = sh.cap;
      ctx.globalAlpha = sPulse;
      ctx.fillRect(sx - sh.r * 0.5, sy - 1.5, sh.r, 2);
      ctx.globalAlpha = 1.0;

      // Spore mote
      const sporeRise = (t * 10 + sx * 2) % 24;
      ctx.fillStyle = sh.cap;
      ctx.globalAlpha = Math.max(0, 1 - sporeRise / 24) * 0.75;
      ctx.fillRect(sx + Math.sin(t * 2 + sx) * 3, sy - 3 - sporeRise, 2, 2);
      ctx.globalAlpha = 1.0;
    }

    // =========================================================================
    // 🔮 4A. NEW FOCAL OBJECT 1: BURIED VOID PSIONIC MONOLITH (x: ~370)
    // =========================================================================
    const monX = 370;
    const monY = fgTop + 20;

    ctx.save();
    // 1. Excavated Runic Bedrock Pit & Displaced Soil
    ctx.fillStyle = 'rgba(2, 6, 23, 0.85)';
    ctx.beginPath();
    ctx.ellipse(monX, monY + 10, 26, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // Fractured stone dais with glowing carved alien glyphs
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(monX - 18, monY + 8);
    ctx.lineTo(monX - 12, monY + 1);
    ctx.lineTo(monX + 12, monY + 1);
    ctx.lineTo(monX + 18, monY + 8);
    ctx.lineTo(monX + 14, monY + 13);
    ctx.lineTo(monX - 14, monY + 13);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Pulsing Ancient Void Runic Glyphs etched on stone, and the
    // floating psionic crystal core (bob/aura/shock-ring/motes) below are
    // both time-varying - rendered live in renderDynamicForeground().

    // 2. Gilded Monolith Arch Braces (Alien gold alloy struts holding the crystal)
    // Left Golden Strut
    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.moveTo(monX - 14, monY + 4);
    ctx.lineTo(monX - 8, monY - 14);
    ctx.lineTo(monX - 5, monY - 14);
    ctx.lineTo(monX - 10, monY + 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(monX - 8, monY - 14, 3, 3);
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(monX - 7, monY - 13, 1.5, 1.5);

    // Right Golden Strut
    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.moveTo(monX + 14, monY + 4);
    ctx.lineTo(monX + 8, monY - 14);
    ctx.lineTo(monX + 5, monY - 14);
    ctx.lineTo(monX + 10, monY + 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(monX + 5, monY - 14, 3, 3);
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(monX + 6, monY - 13, 1.5, 1.5);

    ctx.restore(); // Exit monolith (structural pit/dais/struts only - the
    // floating crystal core, aura, shock ring & orbiting motes render live
    // in renderDynamicForeground())

    // =========================================================================
    // 🪨 4B. SCULPTED NATURAL BASALT BOULDERS & ROCK FORMATIONS (Warm & Mossy)
    // =========================================================================
    const rockFormations = [
      { x: 480, y: fgTop + 24, w: 22, h: 12, moss: '#059669', rust: '#b45309' },
      { x: 700, y: fgTop + 28, w: 18, h: 10, moss: '#047857', rust: '#78350f' },
      { x: 1080, y: fgTop + 25, w: 24, h: 13, moss: '#059669', rust: '#b45309' },
      { x: 1470, y: fgTop + 22, w: 16, h: 9, moss: '#047857', rust: '#78350f' }
    ];

    for (const rf of rockFormations) {
      // 1. Cast shadow onto bedrock
      ctx.fillStyle = 'rgba(2, 6, 23, 0.75)';
      ctx.beginPath();
      ctx.ellipse(rf.x, rf.y + rf.h * 0.4, rf.w * 0.65, 3.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // 2. Main angular rock facet (Rich basalt)
      ctx.fillStyle = '#141a29';
      ctx.beginPath();
      ctx.moveTo(rf.x - rf.w * 0.5, rf.y + rf.h * 0.3);
      ctx.lineTo(rf.x - rf.w * 0.35, rf.y - rf.h * 0.5);
      ctx.lineTo(rf.x + rf.w * 0.2, rf.y - rf.h * 0.6);
      ctx.lineTo(rf.x + rf.w * 0.5, rf.y);
      ctx.lineTo(rf.x + rf.w * 0.3, rf.y + rf.h * 0.4);
      ctx.closePath();
      ctx.fill();

      // 3. Lit upper facet with warm terracotta undertone
      ctx.fillStyle = '#3b4354';
      ctx.beginPath();
      ctx.moveTo(rf.x - rf.w * 0.35, rf.y - rf.h * 0.5);
      ctx.lineTo(rf.x + rf.w * 0.2, rf.y - rf.h * 0.6);
      ctx.lineTo(rf.x + rf.w * 0.4, rf.y - rf.h * 0.1);
      ctx.lineTo(rf.x - rf.w * 0.1, rf.y - rf.h * 0.1);
      ctx.closePath();
      ctx.fill();

      // 4. Emerald Moss Crust on boulder top crest
      ctx.fillStyle = rf.moss;
      ctx.fillRect(rf.x - rf.w * 0.25, rf.y - rf.h * 0.5, rf.w * 0.45, 2.5);
      ctx.fillStyle = '#34d399';
      ctx.fillRect(rf.x - rf.w * 0.15, rf.y - rf.h * 0.5, rf.w * 0.25, 1);

      // 5. Specular top ridge highlight & rust vein
      ctx.strokeStyle = '#64748b';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(rf.x - rf.w * 0.35, rf.y - rf.h * 0.5);
      ctx.lineTo(rf.x + rf.w * 0.2, rf.y - rf.h * 0.6);
      ctx.stroke();

      ctx.fillStyle = rf.rust;
      ctx.fillRect(rf.x - rf.w * 0.1, rf.y - rf.h * 0.1, rf.w * 0.3, 1.5);

      // 6. Small companion pebble
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(rf.x + rf.w * 0.6, rf.y + rf.h * 0.2, 4, 3);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(rf.x + rf.w * 0.6, rf.y + rf.h * 0.2, 4, 1);
    }

    // =========================================================================
    // 🛰️ 4C. CRASHED RECON SATELLITE WITH OCCASIONAL SPARKS (x: ~880)
    // =========================================================================
    const satX = 880;
    const satY = fgTop + 21;

    ctx.save();
    // 1. Impact furrow in bedrock
    ctx.fillStyle = 'rgba(2, 6, 23, 0.85)';
    ctx.beginPath();
    ctx.ellipse(satX + 4, satY + 10, 24, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(satX - 8, satY + 6, 18, 5); // Displaced dirt rim

    // 2. Tilted Satellite Hull (wedged into ground at -28 degrees)
    ctx.save();
    ctx.translate(satX, satY);
    ctx.rotate(-0.48);

    // Cylindrical chassis body
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-10, -18, 20, 26);
    ctx.fillStyle = '#334155';
    ctx.fillRect(-8, -16, 16, 22);

    // Gold thermal foil blanket wrap on lower section
    ctx.fillStyle = '#b45309';
    ctx.fillRect(-8, -4, 16, 10);
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(-7, -3, 14, 2);
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(-7, 1, 14, 1.5);

    // Titanium upper hull plate & specular highlight
    ctx.fillStyle = '#64748b';
    ctx.fillRect(-8, -16, 16, 3);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(-6, -16, 12, 1);

    // Stencil label "ORB"
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.fillRect(-6, -11, 4, 1);
    ctx.fillRect(0, -11, 4, 1);

    // Crumpled High-Gain Parabolic Dish on front
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.ellipse(-12, -10, 5, 8, 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.ellipse(-12, -10, 4, 7, 0.3, 0, Math.PI * 2);
    ctx.stroke();

    // 3. Bent Solar Panel Array Wing (Sticking upward and to the right)
    ctx.fillStyle = '#020617';
    ctx.fillRect(8, -12, 28, 9);
    // Dark blue solar cells
    ctx.fillStyle = '#1e3a8a';
    ctx.fillRect(9, -11, 26, 7);
    ctx.fillStyle = '#0284c7';
    for (let sc = 0; sc < 3; sc++) {
      ctx.fillRect(11 + sc * 8, -10, 6, 5);
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(11 + sc * 8, -10, 6, 1);
    }
    // Solar panel frame strut
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(8, -13, 28, 1.5);

    // Severed power conduit with copper wire tip
    ctx.strokeStyle = '#ea580c';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(34, -8);
    ctx.lineTo(40, -10);
    ctx.lineTo(44, -7);
    ctx.stroke();

    // Idle Telemetry Emergency LED mount
    ctx.fillStyle = '#065f46';
    ctx.fillRect(-5, -7, 2.5, 2.5);

    ctx.restore(); // Exit satellite local rotation
    ctx.restore();

    // =========================================================================
    // 🤖 4D. NEW FOCAL OBJECT 2: CRASHED HEAVY MECH CRAWLER & AUTOCANNON (x: ~1240)
    // =========================================================================
    const mechX = 1240;
    const mechY = fgTop + 21;

    ctx.save();
    // 1. Heavy Impact Trench & Scorched Fractured Bedrock
    ctx.fillStyle = 'rgba(2, 6, 23, 0.9)';
    ctx.beginPath();
    ctx.ellipse(mechX, mechY + 9, 28, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // Scorched metal debris & displaced rock rim
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(mechX - 18, mechY + 6, 14, 4);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(mechX + 6, mechY + 7, 16, 3);
    ctx.fillStyle = '#78350f'; // Rust
    ctx.fillRect(mechX - 14, mechY + 8, 6, 2);

    // 2. Tilted Mech Leg & Autocannon Assembly (Wedged at -34 degrees)
    ctx.save();
    ctx.translate(mechX, mechY);
    ctx.rotate(-0.58);

    // Main Heavy Armored Knee Chassis Housing
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-12, -18, 24, 26);
    ctx.fillStyle = '#334155';
    ctx.fillRect(-10, -16, 20, 22);

    // Heavy Industrial Yellow & Black Hazard Chevron Armor Plate
    ctx.fillStyle = '#facc15';
    ctx.fillRect(-10, -10, 20, 10);
    ctx.fillStyle = '#0f172a';
    // Diagonal hazard stripes
    ctx.beginPath();
    ctx.moveTo(-10, -10); ctx.lineTo(-6, -10); ctx.lineTo(-10, -5); ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-4, -10); ctx.lineTo(0, -10); ctx.lineTo(-8, 0); ctx.lineTo(-10, 0); ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(2, -10); ctx.lineTo(6, -10); ctx.lineTo(-2, 0); ctx.lineTo(-4, 0); ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(8, -10); ctx.lineTo(10, -10); ctx.lineTo(4, 0); ctx.lineTo(2, 0); ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(10, -6); ctx.lineTo(10, -2); ctx.lineTo(8, 0); ctx.lineTo(6, 0); ctx.closePath(); ctx.fill();

    // Heavy Chrome Hydraulic Piston Shaft
    ctx.fillStyle = '#64748b';
    ctx.fillRect(-6, -16, 12, 5);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(-4, -16, 8, 4);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(-2, -16, 4, 1.5);

    // Twin 40mm Heavy Autocannon Barrels (+X / downward into earth)
    // Top Barrel
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(8, -14, 22, 4);
    ctx.fillStyle = '#475569';
    ctx.fillRect(8, -13, 20, 2);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(26, -15, 3, 6); // Fluted Muzzle brake
    // Bottom Barrel
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(8, -7, 22, 4);
    ctx.fillStyle = '#475569';
    ctx.fillRect(8, -6, 20, 2);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(26, -8, 3, 6); // Fluted Muzzle brake

    // Perforated Barrel Cooling Vents
    ctx.fillStyle = '#0f172a';
    for (let v = 0; v < 3; v++) {
      ctx.fillRect(12 + v * 4, -13.5, 2, 3);
      ctx.fillRect(12 + v * 4, -6.5, 2, 3);
    }

    // Idle unlit amber dome
    ctx.fillStyle = '#92400e';
    ctx.fillRect(-3, -20, 6, 3);

    ctx.restore(); // Exit mech rotation

    // Severed polymer hydraulic hose
    ctx.strokeStyle = '#15803d';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(mechX - 14, mechY - 14);
    ctx.lineTo(mechX - 11, mechY - 10);
    ctx.stroke();

    ctx.restore();

    // --- VIGNETTE 3: Eastern Relic Outcrop (x: ~1360-1410) ---
    // (A solitary weathered hull plate and an alien fern cluster)
    const v2X = 1360;
    const v2Y = fgTop + 24;

    // Buried weathered armor plate
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(v2X - 22, v2Y - 2, 34, 12);
    ctx.fillStyle = '#334155';
    ctx.fillRect(v2X - 20, v2Y, 30, 8);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(v2X - 18, v2Y + 1, 26, 1.5);
    ctx.fillStyle = '#78350f'; // Rust
    ctx.fillRect(v2X + 4, v2Y + 2, 4, 6);

    // Stencil marking "07"
    ctx.fillStyle = 'rgba(203, 213, 225, 0.4)';
    ctx.fillRect(v2X - 14, v2Y + 3, 5, 1);
    ctx.fillRect(v2X - 6, v2Y + 3, 5, 1);
  },

  renderForegroundCritters(ctx, battlefield, nowSec = null) {
    if (!battlefield) return;
    const t = nowSec !== null ? nowSec : (performance.now() * 0.001);
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const lowerY = laneY + laneH;
    const fgTop = lowerY + 16;
    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (this.height + 40))) : (this.height + 40);
    const visibleBottom = Math.max(this.height, rawBottom + 20);
    const fgH = visibleBottom - fgTop;

    // 🐾 5. LIVING AMBIENT FAUNA ANIMATIONS (Paced, Rare, and Delightful: Paradigma 9)
    // Spawns every 6-8 seconds, max 2 concurrent critters for lively natural ambience
    this.critterTimer = (this.critterTimer || 0) + (1 / 60);
    if (this.critterTimer >= 7.0) {
      this.critterTimer = 0;
      if (this.foregroundCritters.length < 2 && Math.random() < 0.85) {
        // Medium tier restricts the roster to beetle & rodent (Käfer &
        // Sandratten) per PERFORMANCE_PARADIGMS.md §10; high/ultra keep all 5 species.
        const types = this.quality === 'medium' ? ['beetle', 'rodent'] : ['beetle', 'rodent', 'lizard', 'moth', 'crab'];
        const chosen = types[Math.floor(Math.random() * types.length)];
        const dir = Math.random() > 0.5 ? 1 : -1;
        const isFlying = chosen === 'moth';

        // Calibrated ground Y on visible shelf (498 - 516px)
        const groundY = Math.min(this.height - 24, Math.max(lowerY + 8, fgTop + 2 + Math.random() * 12));
        const spawnY = isFlying ? (groundY - 18) : groundY;

        this.foregroundCritters.push({
          type: chosen,
          x: dir === 1 ? -40 : this.width + 40,
          y: spawnY,
          dir: dir,
          speed: chosen === 'rodent' ? 52 : (chosen === 'lizard' ? 40 : (chosen === 'moth' ? 30 : 26)),
          animTimer: Math.random() * 10,
          pauseTimer: 0,
          actionState: 'walk'
        });
      }
    }

    // Update and render active living critters
    for (let c = this.foregroundCritters.length - 1; c >= 0; c--) {
      const cr = this.foregroundCritters[c];
      cr.animTimer += (1 / 60);

      // Natural pause / look / forage behaviors
      if (cr.pauseTimer > 0) {
        cr.pauseTimer -= (1 / 60);
      } else {
        // Occasional spontaneous pauses
        if (Math.random() < 0.005 && cr.x > 150 && cr.x < this.width - 150) {
          cr.pauseTimer = 0.8 + Math.random() * 0.9;
        } else {
          cr.x += cr.dir * cr.speed * (1 / 60);
        }
      }

      // Remove when off-screen
      if ((cr.dir === 1 && cr.x > this.width + 60) || (cr.dir === -1 && cr.x < -60)) {
        this.foregroundCritters.splice(c, 1);
        continue;
      }

      ctx.save();
      ctx.translate(cr.x, cr.y);
      if (cr.dir === -1) ctx.scale(-1, 1);

      if (cr.type === 'beetle') {
        // 🪲 1. Dune Trilobite Beetle (Bronze segmented carapace, twitching antennae, multi-legs)
        const legStep = Math.sin(cr.animTimer * 26) > 0 ? 1.5 : -1.5;
        // Legs
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(-7, 3 + legStep, 3, 3);
        ctx.fillRect(-1, 3 - legStep, 3, 3);
        ctx.fillRect(5, 3 + legStep, 3, 3);
        // Carapace Shell
        ctx.fillStyle = '#78350f';
        ctx.fillRect(-7, -4, 13, 7);
        ctx.fillStyle = '#b45309';
        ctx.fillRect(-5, -5, 9, 5);
        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(-3, -5, 5, 2);
        // Head & Cyan Glowing Eyes
        ctx.fillStyle = '#451a03';
        ctx.fillRect(6, -3, 4, 5);
        ctx.fillStyle = '#00f0ff';
        ctx.fillRect(8, -4, 2, 2);
        // Antennae
        ctx.fillStyle = '#00f0ff';
        ctx.fillRect(9, -6 + Math.sin(cr.animTimer * 14), 3, 1);
      } else if (cr.type === 'rodent') {
        // 🐀 2. Desert Jerboa Kangaroo Hopper (Big ears, fluffy tail, energetic hops)
        const hopY = cr.pauseTimer > 0 ? 0 : Math.abs(Math.sin(cr.animTimer * 16)) * 5;
        const pawStep = Math.sin(cr.animTimer * 22) > 0 ? 1 : -1;
        // Long Tail
        ctx.fillStyle = '#57534e';
        ctx.fillRect(-14, -2 - hopY + Math.sin(cr.animTimer * 14) * 2, 8, 2);
        ctx.fillStyle = '#fafaf9'; // Fluffy white tuft
        ctx.fillRect(-18, -3 - hopY + Math.sin(cr.animTimer * 14) * 2, 4, 4);
        // Body
        ctx.fillStyle = '#78716c';
        ctx.fillRect(-6, -6 - hopY, 12, 8);
        ctx.fillStyle = '#a8a29e';
        ctx.fillRect(-3, -8 - hopY, 8, 6);
        // Head & Big Ears
        ctx.fillStyle = '#e7e5e4';
        ctx.fillRect(5, -7 - hopY, 6, 6);
        ctx.fillStyle = '#f43f5e'; // Pink inner ear
        ctx.fillRect(4, -12 - hopY, 3, 5);
        ctx.fillStyle = '#1c1917'; // Eye
        ctx.fillRect(8, -6 - hopY, 2, 2);
        // Paws
        ctx.fillStyle = '#44403c';
        ctx.fillRect(-3, 2 - hopY + pawStep, 3, 3);
        ctx.fillRect(4, 2 - hopY - pawStep, 3, 3);
      } else if (cr.type === 'lizard') {
        // 🦎 3. Bioluminescent Sand Gecko (6-legged sleek lizard with glowing stripes & tongue flick)
        const wiggle = Math.sin(cr.animTimer * 18) * 2;
        const legWave = Math.sin(cr.animTimer * 20) > 0 ? 1 : -1;
        // Wavy Tail
        ctx.fillStyle = '#065f46';
        ctx.fillRect(-16, -1 + wiggle, 9, 3);
        ctx.fillStyle = '#34d399'; // Glowing tail tip
        ctx.fillRect(-20, -1 + wiggle * 1.5, 4, 3);
        // 6 Legs
        ctx.fillStyle = '#064e3b';
        ctx.fillRect(-6, 2 + legWave, 3, 3);
        ctx.fillRect(0, 2 - legWave, 3, 3);
        ctx.fillRect(6, 2 + legWave, 3, 3);
        // Body
        ctx.fillStyle = '#047857';
        ctx.fillRect(-6, -4, 14, 6);
        ctx.fillStyle = '#10b981';
        ctx.fillRect(-4, -5, 10, 4);
        // Glowing Dorsal Spots
        ctx.fillStyle = '#67e8f9';
        ctx.fillRect(-2, -5, 2, 2);
        ctx.fillRect(2, -5, 2, 2);
        // Head & Eyes
        ctx.fillStyle = '#065f46';
        ctx.fillRect(8, -4, 6, 5);
        ctx.fillStyle = '#facc15'; // Golden eyes
        ctx.fillRect(11, -5, 2, 2);
        // Occasional Tongue Flick
        if (cr.pauseTimer > 0.4 && Math.sin(cr.animTimer * 12) > 0.4) {
          ctx.fillStyle = '#f43f5e';
          ctx.fillRect(14, -2, 5, 1.5);
          ctx.fillRect(18, -3, 2, 1);
          ctx.fillRect(18, -1, 2, 1);
        }
      } else if (cr.type === 'moth') {
        // 🦋 4. Luminescent Spore Moth (Ethereal fluttering wings & bio-trail)
        const wingFlap = Math.sin(cr.animTimer * 30);
        const floatY = Math.sin(cr.animTimer * 5) * 8;
        // Wing Span (Upper & Lower)
        ctx.fillStyle = 'rgba(232, 121, 249, 0.85)';
        ctx.beginPath();
        ctx.ellipse(0, -5 + floatY, 8, Math.abs(wingFlap) * 9 + 2, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#38bdf8';
        ctx.beginPath();
        ctx.ellipse(2, -4 + floatY, 4, Math.abs(wingFlap) * 5 + 1, 0, 0, Math.PI * 2);
        ctx.fill();
        // Body
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-1, -6 + floatY, 3, 8);
        // Glowing Spore Dust Trail
        ctx.fillStyle = 'rgba(103, 232, 249, 0.5)';
        ctx.fillRect(-6, -5 + floatY + Math.sin(t * 8) * 3, 2, 2);
        ctx.fillRect(-11, -4 + floatY + Math.cos(t * 8) * 3, 1.5, 1.5);
      } else if (cr.type === 'crab') {
        // 🦀 5. Rock Scuttler Crab (Sideways scamper, clicking pincers)
        const clawSnap = Math.sin(cr.animTimer * 14) > 0 ? 1 : 0;
        const crabStep = Math.sin(cr.animTimer * 24) > 0 ? 1 : -1;
        // Legs
        ctx.fillStyle = '#7c2d12';
        ctx.fillRect(-7, 3 + crabStep, 3, 3);
        ctx.fillRect(4, 3 - crabStep, 3, 3);
        // Shell
        ctx.fillStyle = '#c2410c';
        ctx.fillRect(-6, -4, 12, 7);
        ctx.fillStyle = '#ea580c';
        ctx.fillRect(-4, -5, 8, 4);
        // Pincers
        ctx.fillStyle = '#f97316';
        ctx.fillRect(-8, -4 - clawSnap, 3, 4);
        ctx.fillRect(5, -4 - clawSnap, 3, 4);
        // Eyes
        ctx.fillStyle = '#020617';
        ctx.fillRect(-3, -7, 2, 2);
        ctx.fillRect(1, -7, 2, 2);
      } else {
        // 🛸 6. Scavenger Micro-Probe Drone (Hovering with cyan scanning light)
        const hoverBob = Math.sin(cr.animTimer * 6) * 4;
        ctx.fillStyle = '#eab308';
        ctx.fillRect(-4, -4 + hoverBob, 9, 8);
        ctx.fillStyle = '#fde047';
        ctx.fillRect(-2, -6 + hoverBob, 5, 3);
        ctx.fillStyle = '#38bdf8'; // Glowing sensor eye
        ctx.fillRect(3, -2 + hoverBob, 3, 3);
        // Downward micro scanner cone
        ctx.fillStyle = 'rgba(56, 189, 248, 0.22)';
        ctx.beginPath();
        ctx.moveTo(3, 2 + hoverBob);
        ctx.lineTo(14, 18 + hoverBob);
        ctx.lineTo(-6, 18 + hoverBob);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }

    // 7. FLOATING AMBIENT PARTICLES (Drifting spores, motes & energy glints;
    // medium halves the count per PERFORMANCE_PARADIGMS.md §10)
    const ambientParticleCount = (this.quality === 'medium') ? 16 : 32;
    for (let i = 0; i < ambientParticleCount; i++) {
      const px = (i * 57 + t * (18 + (i % 12))) % this.width;
      const py = laneY + 15 + ((i * 31 + Math.sin(t * 1.8 + i) * 22) % (laneH - 30));
      const pColor = i % 4 === 0 ? '#00f0ff' : (i % 4 === 1 ? '#facc15' : (i % 4 === 2 ? '#4ade80' : '#38bdf8'));
      ctx.fillStyle = pColor;
      ctx.globalAlpha = 0.35 + Math.sin(t * 2 + i) * 0.25;
      ctx.fillRect(px, py, 2.5, 2.5);
    }
    ctx.globalAlpha = 1.0;
  },

  // 🌿 DYNAMIC FOREGROUND OVERLAYS (Vignettes, sparks, coolant drips, fern sway)
  renderDynamicForeground(ctx, battlefield, nowSec = null, customBounds = null) {
    if (this.quality === 'low') return;
    const t = nowSec !== null ? nowSec : (performance.now() * 0.001);
    const laneY = battlefield ? battlefield.laneY : 176;
    const laneH = battlefield ? battlefield.laneHeight : 320;
    const lowerY = laneY + laneH;
    const fgTop = lowerY + 16;

    // 1. Crashed Satellite Telemetry Beacon & Arc Sparks (x: ~360)
    const satX = 360;
    const satY = fgTop + 24;
    ctx.save();
    ctx.translate(satX, satY);
    ctx.rotate(0.35);

    const beaconPulse = Math.sin(t * 4) > 0.3 ? 1.0 : 0.0;
    if (beaconPulse > 0) {
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(-5, -7, 2.5, 2.5);
      ctx.fillStyle = 'rgba(74, 222, 128, 0.5)';
      ctx.fillRect(-7, -9, 6.5, 6.5);
    }
    ctx.restore();

    // Electrical arc sparks flying from satellite wire
    const sparkCycle = (t * 1.5) % 4.0;
    if (sparkCycle < 0.65) {
      const sparkProg = sparkCycle / 0.65;
      const arcX = satX + 26;
      const arcY = satY - 24;

      ctx.strokeStyle = Math.random() > 0.5 ? '#ffffff' : '#38bdf8';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(arcX, arcY);
      ctx.lineTo(arcX + (Math.random() * 8 - 4), arcY + (Math.random() * 8 - 4));
      ctx.lineTo(arcX + (Math.random() * 12 - 6), arcY + (Math.random() * 12 - 6));
      ctx.stroke();

      for (let sp = 0; sp < 3; sp++) {
        const sSeed = sp * 2.1;
        const spVx = Math.cos(sSeed * 3) * 22;
        const spVy = -18 - Math.sin(sSeed * 2) * 16;
        const curSpX = arcX + spVx * sparkProg;
        const curSpY = arcY + spVy * sparkProg + 0.5 * 90 * sparkProg * sparkProg;
        const spAlpha = (1.0 - sparkProg);

        ctx.fillStyle = sp % 2 === 0 ? '#fef08a' : '#f97316';
        ctx.globalAlpha = spAlpha;
        ctx.fillRect(curSpX, curSpY, 2, 2);
      }
      ctx.globalAlpha = 1.0;
    }

    // 2. Crashed Mech Crawler (x: ~1240) - Amber Strobe, Coolant Drip & Steam
    // Puff. Per PERFORMANCE_PARADIGMS.md §10 this coolant-drip/spark-arc prop
    // detail is a high/ultra-only accent, not part of the medium tier.
    if (this.quality === 'high' || this.quality === 'ultra') {
      const mechX = 1240;
      const mechY = fgTop + 21;

      ctx.save();
      ctx.translate(mechX, mechY);
      ctx.rotate(-0.58);

      // Amber Hazard Strobe
      const strobeCycle = (t * 3.2) % 2.0;
      const isStrobeOn = strobeCycle < 0.45;
      if (isStrobeOn) {
        ctx.fillStyle = '#fbbf24';
        ctx.fillRect(-3, -21, 6, 4);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-1, -20, 2, 2);

        ctx.fillStyle = 'rgba(251, 191, 36, 0.4)';
        ctx.beginPath();
        ctx.arc(0, -19, 10, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // Fluorescent Green Coolant Drip & Impact Splash
      const dropCycle = (t * 2.0) % 1.5;
      const dripProg = dropCycle / 1.5;
      const dripOriginX = mechX - 11;
      const dripOriginY = mechY - 10;
      const dripPoolY = mechY + 12;

      const dropY = dripOriginY + (dripPoolY - dripOriginY) * (dripProg * dripProg);
      if (dripProg < 0.92) {
        ctx.fillStyle = '#4ade80';
        ctx.fillRect(dripOriginX - 1, dropY, 2, 3);
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(dripOriginX - 0.5, dropY, 1, 2);
      }

      const poolPulse = 0.6 + 0.4 * Math.sin(t * 4);
      ctx.fillStyle = `rgba(34, 197, 94, ${0.45 * poolPulse})`;
      ctx.beginPath();
      ctx.ellipse(dripOriginX, dripPoolY, 6, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#86efac';
      ctx.fillRect(dripOriginX - 1.5, dripPoolY - 0.5, 3, 1);

      if (dripProg >= 0.88) {
        const splashProg = (dripProg - 0.88) / 0.12;
        ctx.strokeStyle = `rgba(74, 222, 128, ${1 - splashProg})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(dripOriginX, dripPoolY, 4 + splashProg * 6, 2 + splashProg * 2.5, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Pressurized Hydraulic Steam Puff
      const steamMechCycle = (t * 1.3) % 3.8;
      if (steamMechCycle < 0.65) {
        const stProg = steamMechCycle / 0.65;
        const stX = mechX + 4;
        const stY = mechY - 14;
        ctx.fillStyle = `rgba(224, 242, 254, ${(1 - stProg) * 0.4})`;
        ctx.beginPath();
        ctx.arc(stX + Math.sin(t * 3) * 2, stY - stProg * 18, 3 + stProg * 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 3. Solitary Alien Fern Cluster Sway (x: ~1360)
    const v2X = 1360;
    const v2Y = fgTop + 24;
    for (let k = 0; k < 5; k++) {
      const fx = v2X + 16 + k * 5;
      const fHeight = 10 + ((k * 5) % 8);
      const fSway = Math.sin(t * 2.4 + fx * 0.1) * 2.5;
      const fy = v2Y + 12;

      ctx.fillStyle = '#064e3b';
      ctx.fillRect(fx + fSway * 0.4, fy - fHeight, 2, fHeight);
      ctx.fillStyle = '#059669';
      ctx.fillRect(fx + fSway * 0.7, fy - fHeight - 1, 1.5, fHeight + 1);
      ctx.fillStyle = k % 2 === 0 ? '#34d399' : '#67e8f9';
      ctx.fillRect(fx + fSway, fy - fHeight - 2, 2, 2);
    }

    // 4. Void Monolith (x: ~370): pulsing runic glyphs & the floating
    // psionic crystal core (bob, aura, shock ring, orbiting motes). The
    // dais/pit/struts are structural and stay in the static bake; every
    // one of these reads t (breathing pulse / bob / rotation), so it
    // freezes mid-animation if left baked - rendered live here instead.
    {
      const monX = 370;
      const monY = fgTop + 20;

      ctx.save();

      // Pulsing Ancient Void Runic Glyphs etched on stone
      const runePulse = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * 2.6));
      ctx.fillStyle = `rgba(139, 92, 246, ${runePulse * 0.9})`;
      ctx.fillRect(monX - 10, monY + 4, 3, 2);
      ctx.fillRect(monX - 5, monY + 6, 2, 3);
      ctx.fillRect(monX + 3, monY + 4, 4, 2);
      ctx.fillRect(monX + 8, monY + 6, 2, 3);
      ctx.fillStyle = `rgba(0, 240, 255, ${runePulse})`;
      ctx.fillRect(monX - 1, monY + 3, 2, 4);

      // Floating, Levitating Psionic Crystal Shard Core (Oscillating with breathing glow)
      const cryBob = Math.sin(t * 2.8) * 3.2;
      const cryY = monY - 11 + cryBob;

      // Radial Psionic Aura Field
      const cryGrad = ctx.createRadialGradient(monX, cryY, 1, monX, cryY, 20);
      cryGrad.addColorStop(0, `rgba(168, 85, 247, ${0.45 * runePulse})`);
      cryGrad.addColorStop(0.5, `rgba(0, 240, 255, ${0.25 * runePulse})`);
      cryGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = cryGrad;
      ctx.fillRect(monX - 22, cryY - 22, 44, 44);

      // Psionic Harmonic Resonant Shock Ring (Periodic energy wave rippling outward)
      const waveCycle = (t * 1.2) % 3.6;
      if (waveCycle < 0.9) {
        const wProg = waveCycle / 0.9;
        const wRadius = 6 + wProg * 22;
        ctx.strokeStyle = `rgba(0, 240, 255, ${(1 - wProg) * 0.7})`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.ellipse(monX, cryY, wRadius, wRadius * 0.45, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Faceted Crystal Shard Body (Rhombus/Diamond geometry)
      ctx.fillStyle = '#6b21a8';
      ctx.beginPath();
      ctx.moveTo(monX, cryY - 12);
      ctx.lineTo(monX + 6, cryY);
      ctx.lineTo(monX, cryY + 10);
      ctx.lineTo(monX - 6, cryY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#a855f7';
      ctx.beginPath();
      ctx.moveTo(monX, cryY - 12);
      ctx.lineTo(monX - 6, cryY);
      ctx.lineTo(monX, cryY + 10);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#00f0ff';
      ctx.beginPath();
      ctx.moveTo(monX, cryY - 12);
      ctx.lineTo(monX + 5, cryY - 1);
      ctx.lineTo(monX + 1, cryY + 8);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(monX - 0.75, cryY - 11, 1.5, 4);
      ctx.fillStyle = '#f0abfc';
      ctx.fillRect(monX - 2, cryY - 1, 3, 2);

      // Ethereal Orbiting Energy Motes / Plasma Sparks
      const monolithMotes = [
        { speed: 3.2, phase: 0, rx: 14, ry: 5, col: '#00f0ff', sz: 2.0 },
        { speed: -2.4, phase: 2.1, rx: 11, ry: 4, col: '#c084fc', sz: 1.8 },
        { speed: 4.0, phase: 4.3, rx: 13, ry: 6, col: '#fef08a', sz: 1.5 }
      ];
      for (const mt of monolithMotes) {
        const mAngle = t * mt.speed + mt.phase;
        const mX = monX + Math.cos(mAngle) * mt.rx;
        const mY = cryY + Math.sin(mAngle) * mt.ry;
        ctx.fillStyle = mt.col;
        ctx.fillRect(mX - mt.sz / 2, mY - mt.sz / 2, mt.sz, mt.sz);
        ctx.fillStyle = mt.col;
        ctx.globalAlpha = 0.4;
        ctx.fillRect(mX - Math.cos(mAngle) * 3 - 0.5, mY - Math.sin(mAngle) * 1.5 - 0.5, 1.2, 1.2);
        ctx.globalAlpha = 1.0;
      }

      ctx.restore();
    }
  },

  // 🌌 DYNAMIC BACKGROUND ACCENTS (Beacons, Trees, City Lights, Cloud Shadows, Sky Traffic)
  renderDynamicBackground(ctx, battlefield, nowSec = null, customBounds = null) {
    const t = nowSec !== null ? nowSec : (performance.now() * 0.001);
    const laneY = battlefield ? battlefield.laneY : 176;
    const laneH = battlefield ? battlefield.laneHeight : 320;
    const lowerY = laneY + laneH;

    // Visible bounds from camera or custom bounds
    const bounds = customBounds || (this.viewport ? this.viewport.getVisibleBounds() : null);
    const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
    const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (this.height + 40))) : (this.height + 40);
    const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
    const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (this.width + 40))) : (this.width + 40);

    const visibleTop = customBounds ? rawTop : Math.min(0, rawTop - 20);
    const visibleBottom = customBounds ? rawBottom : Math.max(this.height, rawBottom + 20);
    const visibleLeft = customBounds ? rawLeft : Math.min(0, rawLeft - 20);
    const visibleRight = customBounds ? rawRight : Math.max(this.width, rawRight + 20);
    const bgWidth = visibleRight - visibleLeft;

    // 🌟 1. Multi-Tier Twinkling Starfield with Color Accents (medium halves
    // the count per PERFORMANCE_PARADIGMS.md §10's tier-reduction philosophy)
    const starCount = (this.quality === 'medium') ? 33 : 65;
    for (let i = 0; i < starCount; i++) {
      const sx = (i * 37 + 13) % this.width;
      const sy = (i * 23 + 7) % (laneY - 32);
      const twinkle = (Math.sin(t * 2.5 + i * 1.3) + 1) * 0.5;
      const isBig = i % 5 === 0;
      const isCross = i % 13 === 0;
      const starColor = i % 4 === 0 ? '#38bdf8' : (i % 7 === 0 ? '#fde047' : '#ffffff');

      ctx.fillStyle = starColor;
      ctx.globalAlpha = 0.25 + twinkle * 0.7;
      ctx.fillRect(sx, sy, isBig ? 2 : 1, isBig ? 2 : 1);

      // Diffraction cross spike on brightest stars (skipped on low)
      if (isCross && twinkle > 0.6 && this.quality !== 'low') {
        ctx.globalAlpha = (twinkle - 0.6) * 0.8;
        ctx.fillRect(sx - 2, sy, 5, 1);
        ctx.fillRect(sx, sy - 2, 1, 5);
      }
    }
    ctx.globalAlpha = 1.0;

    // Shooting Star / Meteor Streak (every ~7 seconds)
    const meteorCycle = (t * 250) % 1800;
    if (meteorCycle < 350) {
      const mx = meteorCycle * 2.2;
      const my = 15 + meteorCycle * 0.25;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(mx, my);
      ctx.lineTo(mx - 42, my - 8);
      ctx.stroke();
    }

    // 🌌 Sul Swarm Cosmic Violet Nebula Aurora & Bioluminescent Spores
    const hasSulFaction = battlefield?.bases?.western?.race === 'Sul' || battlefield?.bases?.eastern?.race === 'Sul';
    if (hasSulFaction && this.quality !== 'low') {
      const sulSideX = battlefield?.bases?.western?.race === 'Sul' ? visibleLeft + 160 : visibleRight - 160;
      const nebCanvas = getSulNebulaCanvas();
      if (nebCanvas) {
        ctx.drawImage(nebCanvas, sulSideX - 240, visibleTop + 60 - 240);
      }

      // Ambient bio-luminescent spore drift rising on the Sul flank
      for (let sp = 0; sp < 6; sp++) {
        const sporeCycle = (t * 16 + sp * 50) % 160;
        const spX = sulSideX + Math.sin(t * 1.4 + sp * 2) * 45;
        const spY = laneY - 15 - sporeCycle;
        ctx.fillStyle = sp % 2 === 0 ? 'rgba(217, 70, 239, 0.6)' : 'rgba(0, 240, 255, 0.6)';
        ctx.fillRect(spX, spY, 1.5, 1.5);
      }
    }

    // Flying-car light streaks weaving between distant towers
    const cityTrafficCount = (this.quality === 'low') ? 0 : ((this.quality === 'medium') ? 4 : 7);
    for (let s = 0; s < cityTrafficCount; s++) {
      const speed = 30 + (s % 4) * 18;
      const altitude = laneY - 10 - (s % 5) * 9;
      const dir = (s % 2 === 0) ? 1 : -1;
      const travelDist = this.width + 160;
      const prog = (t * speed + s * 210) % travelDist;
      const carX = (dir === 1) ? (-80 + prog) : (this.width + 80 - prog);
      const trailLen = 26 + (s % 3) * 14;

      const streak = getTrafficStreakCanvas(s % 3, dir);
      if (streak) {
        const drawX = dir === 1 ? (carX - trailLen) : carX;
        ctx.drawImage(streak, drawX, altitude - 1, trailLen, 2.6);
      }
    }

    // 🏙️ 2. DYNAMIC SKYLINE LIGHTS & BEACONS (Left Zone 0-500px)
    if (this.quality !== 'low') {
      for (let idx = 0; idx < CITY_TOWERS.length; idx++) {
        const tower = CITY_TOWERS[idx];
        const tx = tower.x;
        const tw = tower.w;
        const th = tower.h;
        const ty = laneY - th;

        // Rooftop Warning Beacon
        if (tower.antenna) {
          const antX = tx + (idx % 2 === 0 ? 14 : tw - 16);
          const beaconBlink = Math.sin(t * 4.5 + idx * 2.1) > 0.1;
          ctx.fillStyle = beaconBlink ? tower.beaconCol : '#1e293b';
          ctx.fillRect(antX - 2, ty - 21, 4, 3);
          if (beaconBlink) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(antX - 1, ty - 20, 2, 1);
          }
        }

        // Helipad Runway Lights
        if (tower.heliPad) {
          const hPadX = tx + tw / 2;
          const hPadY = ty + 1;
          const hBlink = Math.floor(t * 2) % 2 === 0;
          ctx.fillStyle = hBlink ? '#4ade80' : '#15803d';
          ctx.fillRect(hPadX - 12, hPadY - 2, 2, 2);
          ctx.fillRect(hPadX + 10, hPadY - 2, 2, 2);
        }

        // Holographic Tech Sign Pulse
        if (tower.holoSign) {
          const signX = tx + 12;
          const signY = ty + 24;
          const signPulse = 0.65 + 0.35 * Math.sin(t * 3.2 + idx);
          ctx.fillStyle = `rgba(6, 182, 212, ${0.45 * signPulse})`;
          ctx.fillRect(signX, signY, 26, 8);
          ctx.fillStyle = `rgba(255, 255, 255, ${0.8 * signPulse})`;
          ctx.fillRect(signX + 2, signY + 2, 10, 1.5);
          ctx.fillRect(signX + 2, signY + 5, 20, 1.5);
        }

        // Flickering Window Accent Lights (Desktop high/ultra)
        if (this.quality === 'high' || this.quality === 'ultra') {
          const cellW = (tw - 16) / tower.winCols;
          for (let r = 0; r < Math.min(2, tower.winRows); r++) {
            const wy = ty + 10 + r * 9;
            const c = (idx + r * 3) % tower.winCols;
            const wx = tx + 8 + c * cellW;
            const wFlick = Math.sin(t * 2.2 + idx * 1.7 + r * 3.1) > 0.2;
            if (wFlick) {
              ctx.fillStyle = (idx % 2 === 0) ? 'rgba(56, 189, 248, 0.85)' : 'rgba(250, 204, 21, 0.85)';
              ctx.fillRect(wx + 1, wy, Math.max(2, cellW - 3), 3.5);
            }
          }
        }
      }
    }

    // 🌲 3. DYNAMIC ALIEN FLORA & WIND SWAY (Upper Lane Ridge)
    if (this.quality !== 'low') {
      const gustBoost = 1.0 + (this.getWeatherState?.()?.windIntensity || 0) * 1.5;
      const nukeAge = (battlefield && battlefield.isNuked) ? (battlefield.nukeTimer || 0) : 999;
      const treeCount = this.quality === 'medium' ? 6 : TREE_CLUSTERS.length;

      for (let idx = 0; idx < treeCount; idx++) {
        const tree = TREE_CLUSTERS[idx];
        const tx = tree.x;
        const tH = tree.trunkH;
        const hs = tree.hScale;
        const ws = tree.wScale;
        const isBioFlora = battlefield && battlefield.scarType === 'bioplasma';

        if (isBioFlora && nukeAge < 32) {
          if (nukeAge < 14) {
            // Sul Stage 1 (docs/BIO_PLASMA_PLAN.md §7.5): Immediate disintegration to spore dust
            const sporeProg = (t * (1.2 + (idx % 3) * 0.4) + idx * 0.2) % 1.0;
            const sporeAlpha = Math.sin(sporeProg * Math.PI) * 0.65;
            ctx.fillStyle = `rgba(168, 85, 247, ${sporeAlpha})`;
            ctx.fillRect(tx - 6 * ws, laneY - tH - 8 * hs - sporeProg * 14, 12 * ws, 3);
            ctx.fillStyle = `rgba(217, 70, 239, ${sporeAlpha * 0.5})`;
            ctx.fillRect(tx - 3 * ws, laneY - tH - 12 * hs - sporeProg * 20, 6 * ws, 2);
          } else {
            // Sul Stage 2: Bare ground with subtle violet perimeter shimmer
            const bioPulse = 0.35 + 0.35 * Math.sin(t * 3.2 + tx * 0.05);
            ctx.fillStyle = `rgba(168, 85, 247, ${bioPulse * 0.45})`;
            ctx.fillRect(tx - 8 * ws, laneY - tH, 16 * ws, 2.5);
          }
        } else if (nukeAge < 14) {
          // Flame bursts
          const freq1 = 11 + (idx % 5) * 2.5;
          const freq2 = 17 + (idx % 4) * 3.0;
          const baseH = (15 + (idx % 5) * 2.2) * hs;
          const flamePulse1 = Math.abs(Math.sin(t * freq1 + idx * 1.7));
          const flamePulse2 = Math.abs(Math.cos(t * freq2 + idx * 2.3));
          const fHeight = baseH + flamePulse1 * 7 * hs + flamePulse2 * 4 * hs;
          const flameW = (16 + (idx % 3) * 3) * ws;
          const windLean = Math.sin(t * 3.5 + idx * 0.8) * (2.5 * gustBoost);

          ctx.fillStyle = '#ef4444';
          ctx.fillRect(tx - flameW / 2 + windLean, laneY - tH - fHeight, flameW, fHeight);
          ctx.fillStyle = '#f97316';
          ctx.fillRect(tx - (flameW * 0.7) / 2 + windLean * 0.8, laneY - (tH - 2) - fHeight * 0.8, flameW * 0.7, fHeight * 0.8);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(tx - (flameW * 0.4) / 2 + windLean * 0.6, laneY - (tH - 4) - fHeight * 0.55, flameW * 0.4, fHeight * 0.55);
        } else if (nukeAge < 32) {
          // Ember pulses on charred wood
          const emberPulse = 0.5 + 0.5 * Math.sin(t * 5 + tx);
          ctx.fillStyle = `rgba(249, 115, 22, ${0.75 * emberPulse})`;
          ctx.fillRect(tx - 11 * ws, laneY - (tH + 15), 3, 3);
          ctx.fillRect(tx + 14 * ws, laneY - (tH + 11), 3, 3);
        } else if (nukeAge < 50) {
          // Sprouting regrowth
          const growProg = (nukeAge - 32) / 18.0;
          const curW = (12 + growProg * 18) * ws;
          const curH = (8 + growProg * 12) * hs;
          ctx.fillStyle = '#15803d';
          ctx.fillRect(tx - curW / 2, laneY - tH - curH, curW, curH);
          ctx.fillStyle = '#22c55e';
          ctx.fillRect(tx - curW * 0.35, laneY - tH - curH * 1.3, curW * 0.7, curH * 0.7);
        } else {
          // Fully healthy swaying flora
          const windSway = Math.sin(t * 2.5 + tx * 0.02) * (6 * gustBoost);
          const species = tree.species || 'round';

          if (species === 'pine') {
            const baseW = 24 * ws;
            for (let tier = 0; tier < 3; tier++) {
              const tierSway = windSway * (0.5 + tier * 0.35);
              const tierW = baseW - tier * 6 * ws;
              const tierTopY = laneY - tH - (10 + tier * 9) * hs;
              const tierBotY = laneY - tH - (tier === 0 ? 0 : (tier * 9 - 3)) * hs;
              ctx.fillStyle = tier === 2 ? '#4ade80' : (tier === 1 ? '#22c55e' : '#166534');
              ctx.beginPath();
              ctx.moveTo(tx + tierSway, tierTopY);
              ctx.lineTo(tx - tierW / 2 + tierSway, tierBotY);
              ctx.lineTo(tx + tierW / 2 + tierSway, tierBotY);
              ctx.closePath();
              ctx.fill();
            }
          } else if (species === 'crystal') {
            const capW = 26 * ws;
            const capH = 20 * hs;
            const crystalPulse = 0.55 + 0.35 * Math.sin(t * 2.4 + tx * 0.1);
            ctx.fillStyle = '#312e81';
            ctx.beginPath();
            ctx.moveTo(tx + windSway, laneY - tH - capH);
            ctx.lineTo(tx - capW / 2 + windSway, laneY - tH - capH * 0.35);
            ctx.lineTo(tx + windSway, laneY - tH);
            ctx.lineTo(tx + capW / 2 + windSway, laneY - tH - capH * 0.35);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = '#5b21b6';
            ctx.fillRect(tx - capW * 0.22 + windSway, laneY - tH - capH * 0.7, capW * 0.44, capH * 0.4);
            ctx.fillStyle = `rgba(196, 181, 253, ${crystalPulse})`;
            ctx.fillRect(tx - 2 + windSway, laneY - tH - capH * 0.62, 4, 4);
          } else if (species === 'willow') {
            const canopyW = 34 * ws;
            ctx.fillStyle = '#166534';
            ctx.fillRect(tx - canopyW / 2 + windSway, laneY - (tH + 12 * hs), canopyW, 12 * hs);
            ctx.fillStyle = '#22c55e';
            ctx.fillRect(tx - canopyW * 0.35 + windSway * 1.1, laneY - (tH + 18 * hs), canopyW * 0.7, 8 * hs);
            ctx.fillStyle = '#4ade80';
            for (let d = -1; d <= 1; d++) {
              const dropSway = Math.sin(t * 2.2 + tx + d * 1.7) * 3;
              ctx.fillRect(tx + d * 11 * ws + dropSway, laneY - tH - 2, 2, 9 * hs);
            }
          } else {
            ctx.fillStyle = '#15803d';
            ctx.fillRect(tx - 12 * ws + windSway, laneY - (tH + 16 * hs), 30 * ws, 18 * hs);
            ctx.fillStyle = '#22c55e';
            ctx.fillRect(tx - 8 * ws + windSway * 1.2, laneY - (tH + 24 * hs), 22 * ws, 14 * hs);
            ctx.fillStyle = '#86efac';
            ctx.fillRect(tx - 4 * ws + windSway * 1.4, laneY - (tH + 28 * hs), 14 * ws, 8 * hs);
          }
        }
      }
    }

    // 4. Drifting 8-Bit Sky Clouds
    if (this.quality !== 'low') {
      ctx.fillStyle = 'rgba(100, 145, 195, 0.16)';
      const cloudSpeed1 = 14;
      for (let i = 0; i < 8; i++) {
        const cx = (i * 240 + t * cloudSpeed1) % (this.width + 200) - 100;
        const cy = 16 + (i % 3) * 20;
        ctx.fillRect(cx, cy, 76, 16);
        ctx.fillRect(cx + 14, cy - 8, 48, 24);
        ctx.fillRect(cx + 26, cy - 14, 24, 30);
      }

      ctx.fillStyle = 'rgba(140, 185, 235, 0.24)';
      const cloudSpeed2 = 28;
      for (let i = 0; i < 6; i++) {
        const cx = (i * 320 + t * cloudSpeed2) % (this.width + 260) - 130;
        const cy = 44 + (i % 2) * 26;
        ctx.fillRect(cx, cy, 94, 18);
        ctx.fillRect(cx + 20, cy - 10, 54, 28);
      }
    }

    // ☁️ 5. PROJECTED CLOUD SHADOWS ON THE BATTLEFIELD FLOOR (LUT Blitting: Paradigma 8)
    if (this.quality !== 'low') {
      const shadowLUT = getCloudShadowLUT();
      const floorRowH = laneH / 10;
      const floorTopY = laneY + floorRowH;
      const shadowCount = this.quality === 'medium' ? 2 : 5;

      if (shadowLUT) {
        for (let c = 0; c < shadowCount; c++) {
          const cProg = (t * 20 + c * 360) % (this.width + 450) - 220;
          const cY = floorTopY + 25 + (c % 3) * 85;
          const cW = 200 + (c % 2) * 90;
          const cH = 55 + (c % 3) * 18;
          ctx.drawImage(shadowLUT, cProg - cW / 2, cY - cH / 2, cW, cH);
        }
      }

      // Flickering Celestial God Rays (Desktop high/ultra only)
      if (this.quality === 'high' || this.quality === 'ultra') {
        const moonFlicker = 0.82 + 0.22 * Math.sin(t * 1.25);
        const moonSway = Math.sin(t * 0.85) * 22;
        const beam1Alpha = (0.06 * moonFlicker);
        ctx.fillStyle = `rgba(224, 242, 254, ${beam1Alpha})`;
        ctx.beginPath();
        ctx.moveTo(1160, floorTopY);
        ctx.lineTo(1300, floorTopY);
        ctx.lineTo(760 + moonSway, lowerY);
        ctx.lineTo(560 + moonSway, lowerY);
        ctx.closePath();
        ctx.fill();
      }
    }

    // 🚨 6. ROTATING RED HAZARD BEACONS (Top & Bottom Centerline: Paradigma 7)
    if (this.quality !== 'low') {
      const midX = this.width / 2;
      const rotSpeed = 4.5;
      const rotAngleTop = (t * rotSpeed);
      const rotAngleBot = (t * rotSpeed + 1.2);

      // Upper Beacon (y ~ 172)
      const topBx = midX;
      const topBy = laneY - 4;

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      if (!this._topBeamGrad) {
        const g = ctx.createRadialGradient(0, 0, 2, 36, 0, 32);
        g.addColorStop(0, 'rgba(239, 68, 68, 0.45)');
        g.addColorStop(0.5, 'rgba(220, 38, 38, 0.18)');
        g.addColorStop(1, 'rgba(185, 28, 28, 0)');
        this._topBeamGrad = g;
      }
      ctx.save();
      ctx.translate(topBx, topBy);
      ctx.rotate(rotAngleTop);
      ctx.fillStyle = this._topBeamGrad;
      ctx.beginPath();
      ctx.ellipse(16, 0, 26, 11, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Ambient Ground Red Wash
      if (!this._topGlowGrad || this._topGlowBx !== topBx || this._topGlowBy !== topBy) {
        this._topGlowBx = topBx;
        this._topGlowBy = topBy;
        const g = ctx.createRadialGradient(topBx, topBy, 1, topBx, topBy, 22);
        g.addColorStop(0, 'rgba(239, 68, 68, 0.32)');
        g.addColorStop(1, 'rgba(239, 68, 68, 0)');
        this._topGlowGrad = g;
      }
      ctx.fillStyle = this._topGlowGrad;
      ctx.beginPath();
      ctx.ellipse(topBx, topBy + 2, 22, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Reflector glint inside dome & direct flash when facing viewer
      const topReflectOffset = Math.sin(rotAngleTop) * 2;
      ctx.fillStyle = '#fca5a5';
      ctx.fillRect(topBx + topReflectOffset - 1, topBy - 6, 2, 4);

      const topFacing = Math.sin(rotAngleTop);
      if (topFacing > 0.3) {
        const topFlashAlpha = (topFacing - 0.3) / 0.7;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.85 * topFlashAlpha})`;
        ctx.fillRect(topBx - 1, topBy - 5, 2, 2);
        ctx.fillStyle = `rgba(239, 68, 68, ${0.65 * topFlashAlpha})`;
        ctx.beginPath();
        ctx.arc(topBx, topBy - 4, 9, 0, Math.PI * 2);
        ctx.fill();
      }

      // Lower Beacon (y ~ 498)
      const botBx = midX;
      const botBy = lowerY + 2;

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      if (!this._botBeamGrad) {
        const g = ctx.createRadialGradient(0, 0, 4, 80, 0, 70);
        g.addColorStop(0, 'rgba(239, 68, 68, 0.55)');
        g.addColorStop(0.45, 'rgba(220, 38, 38, 0.22)');
        g.addColorStop(1, 'rgba(185, 28, 28, 0)');
        this._botBeamGrad = g;
      }
      ctx.save();
      ctx.translate(botBx, botBy);
      ctx.rotate(rotAngleBot);
      ctx.fillStyle = this._botBeamGrad;
      ctx.beginPath();
      ctx.ellipse(35, 0, 60, 26, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Ambient Ground Red Wash
      if (!this._botGlowGrad || this._botGlowBx !== botBx || this._botGlowBy !== botBy) {
        this._botGlowBx = botBx;
        this._botGlowBy = botBy;
        const g = ctx.createRadialGradient(botBx, botBy, 2, botBx, botBy, 50);
        g.addColorStop(0, 'rgba(239, 68, 68, 0.40)');
        g.addColorStop(1, 'rgba(239, 68, 68, 0)');
        this._botGlowGrad = g;
      }
      ctx.fillStyle = this._botGlowGrad;
      ctx.beginPath();
      ctx.ellipse(botBx, botBy + 4, 50, 20, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Reflector glint inside dome & direct flash when facing viewer
      const botReflectOffset = Math.sin(rotAngleBot) * 4.5;
      ctx.fillStyle = '#fecaca';
      ctx.fillRect(botBx + botReflectOffset - 1.5, botBy - 12, 3, 7);

      const botFacing = Math.sin(rotAngleBot);
      if (botFacing > 0.25) {
        const botFlashAlpha = (botFacing - 0.25) / 0.75;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.95 * botFlashAlpha})`;
        ctx.fillRect(botBx - 2, botBy - 10, 4, 4);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(botBx - 1, botBy - 9, 2, 2);

        ctx.fillStyle = `rgba(239, 68, 68, ${0.75 * botFlashAlpha})`;
        ctx.beginPath();
        ctx.arc(botBx, botBy - 8, 18, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(254, 202, 202, ${0.55 * botFlashAlpha})`;
        ctx.beginPath();
        ctx.arc(botBx, botBy - 8, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 🌋 7. POST-NUCLEAR APOCALYPTIC ASH CANOPY & SEARING THERMAL SKY
    if (battlefield && battlefield.isNuked && (battlefield.nukeTimer || 0) < 52) {
      const nukeAge = battlefield.nukeTimer || 0;
      const falloutIntensity = Math.min(1.0, nukeAge / 1.5) * Math.max(0, 1.0 - (nukeAge / 52.0));
      
      ctx.save();
      const isBioPlasmaSky = battlefield.scarType === 'bioplasma';

      if (isBioPlasmaSky) {
        // Sul Bio-Plasma Violet Aurora (docs/BIO_PLASMA_PLAN.md §7.5)
        const skyAuroraGrad = ctx.createLinearGradient(0, visibleTop, 0, laneY);
        skyAuroraGrad.addColorStop(0, `rgba(59, 7, 100, ${0.75 * falloutIntensity})`);
        skyAuroraGrad.addColorStop(0.4, `rgba(168, 85, 247, ${0.45 * falloutIntensity})`);
        skyAuroraGrad.addColorStop(0.75, `rgba(217, 70, 239, ${0.20 * falloutIntensity})`);
        skyAuroraGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = skyAuroraGrad;
        ctx.fillRect(visibleLeft, visibleTop, bgWidth, laneY - visibleTop);

        const ribbonCount = 3;
        for (let r = 0; r < ribbonCount; r++) {
          const rSpeed = 0.25 + r * 0.12; // Ethereal slower sway
          const rWaveFreq = 0.0025 + r * 0.0012;
          const rBaseY = 22 + r * 26;
          
          ctx.beginPath();
          ctx.moveTo(visibleLeft, visibleTop);

          for (let x = visibleLeft; x <= visibleRight; x += 16) {
            const wave1 = Math.sin(t * rSpeed + x * rWaveFreq) * 18;
            const wave2 = Math.cos(t * (rSpeed * 1.2) + x * (rWaveFreq * 1.5)) * 10;
            const ribbonY = rBaseY + wave1 + wave2;
            ctx.lineTo(x, Math.max(visibleTop, ribbonY));
          }

          ctx.lineTo(visibleRight, visibleTop);
          ctx.closePath();

          const auroraColors = [
            `rgba(59, 7, 100, ${0.52 * falloutIntensity})`,
            `rgba(147, 51, 234, ${0.35 * falloutIntensity})`,
            `rgba(217, 70, 239, ${0.22 * falloutIntensity})`
          ];
          ctx.fillStyle = auroraColors[r % auroraColors.length];
          ctx.fill();
        }

        // Drifting ethereal micro-spore dust (violet & bio-green)
        for (let f = 0; f < 20; f++) {
          const sporeSeed = f * 31.41;
          const sx = (sporeSeed * 19 + t * (15 + (f % 3) * 6)) % (this.width + 60) - 30;
          const sy = (sporeSeed * 13 + t * (20 + (f % 4) * 8)) % (laneY + 30);
          const sPulse = 0.4 + 0.6 * Math.sin(t * 3 + f);
          ctx.globalAlpha = (f % 2 === 0 ? 0.55 : 0.3) * falloutIntensity * sPulse;
          ctx.fillStyle = f % 2 === 0 ? '#c084fc' : '#84cc16';
          ctx.fillRect(sx, sy, 2, 2);
        }
        ctx.globalAlpha = 1.0;
        ctx.restore();
        return;
      }

      const skyFalloutGrad = ctx.createLinearGradient(0, visibleTop, 0, laneY);
      skyFalloutGrad.addColorStop(0, `rgba(15, 23, 42, ${0.92 * falloutIntensity})`);
      skyFalloutGrad.addColorStop(0.35, `rgba(69, 26, 3, ${0.75 * falloutIntensity})`);
      skyFalloutGrad.addColorStop(0.7, `rgba(185, 28, 28, ${0.45 * falloutIntensity})`);
      skyFalloutGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = skyFalloutGrad;
      ctx.fillRect(visibleLeft, visibleTop, bgWidth, laneY - visibleTop);

      const ribbonCount = 3;
      for (let r = 0; r < ribbonCount; r++) {
        const rSpeed = 0.4 + r * 0.2;
        const rWaveFreq = 0.003 + r * 0.0015;
        const rBaseY = 18 + r * 28;
        
        ctx.beginPath();
        ctx.moveTo(visibleLeft, visibleTop);

        for (let x = visibleLeft; x <= visibleRight; x += 16) {
          const wave1 = Math.sin(t * rSpeed + x * rWaveFreq) * 20;
          const wave2 = Math.cos(t * (rSpeed * 1.3) + x * (rWaveFreq * 1.6)) * 12;
          const ribbonY = rBaseY + wave1 + wave2;
          ctx.lineTo(x, Math.max(visibleTop, ribbonY));
        }

        ctx.lineTo(visibleRight, visibleTop);
        ctx.closePath();

        const plumeColors = [
          `rgba(30, 41, 59, ${0.65 * falloutIntensity})`,
          `rgba(124, 45, 18, ${0.42 * falloutIntensity})`,
          `rgba(153, 27, 27, ${0.32 * falloutIntensity})`
        ];
        ctx.fillStyle = plumeColors[r % plumeColors.length];
        ctx.fill();
      }

      const dischargeFreq = (t * 4.5) % 3.0;
      if (dischargeFreq < 0.25 && falloutIntensity > 0.2) {
        const boltX = (Math.sin(t * 17) * 0.5 + 0.5) * (this.width - 200) + 100;
        ctx.strokeStyle = '#fde047';
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.75 * falloutIntensity;
        ctx.beginPath();
        ctx.moveTo(boltX, 0);
        ctx.lineTo(boltX + 18, 25);
        ctx.lineTo(boltX - 12, 45);
        ctx.lineTo(boltX + 8, 70);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }

      ctx.fillStyle = '#f97316';
      for (let f = 0; f < 25; f++) {
        const ashSeed = f * 37.19;
        const ax = (ashSeed * 17 + t * (25 + (f % 4) * 8)) % (this.width + 60) - 30;
        const ay = (ashSeed * 11 + t * (40 + (f % 5) * 12)) % (laneY + 40);
        const aPulse = 0.4 + 0.6 * Math.sin(t * 4 + f);
        ctx.globalAlpha = (f % 2 === 0 ? 0.6 : 0.3) * falloutIntensity * aPulse;
        ctx.fillStyle = f % 3 === 0 ? '#fef08a' : (f % 3 === 1 ? '#f97316' : '#1e293b');
        ctx.fillRect(ax, ay, f % 2 === 0 ? 2.5 : 1.5, f % 2 === 0 ? 2.5 : 1.5);
      }
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    // ══════════════════════════════════════════════════════════════════
    // 🌧️ ANIMATED RAIN PUDDLES, AMBIENT MOTES, SEARCHLIGHTS, VENTS,
    // REFINERY/ELEVATOR/PYLON/HIVE ACCENTS & STADIUM FLOODLIGHT TOWERS
    // Moved out of the one-time static bake (Paradigma 7): these all read
    // `t`/`nowSec` (and the puddles additionally read live particles.lights
    // & battlefield.units), so baking them once at t=0 either froze them
    // mid-animation or - for several event-cycle effects below - never
    // rendered at all (their t=0 trigger condition evaluated false).
    // ══════════════════════════════════════════════════════════════════

    const moonFlickerAccent = 0.82 + 0.22 * Math.sin(t * 1.25) + 0.08 * Math.sin(t * 3.6 + 1.4) + 0.04 * Math.sin(t * 8.7 + 2.1);

    // 🚀 Autonomous Hover Cargo Speeder / Drone Flight (every ~16 seconds)
    const droneCycle = (t * 40) % 650;
    if (droneCycle < 520) {
      const dx = -20 + droneCycle;
      const dy = laneY - 65 + Math.sin(droneCycle * 0.03) * 6;

      const ionGrad = ctx.createLinearGradient(dx, dy, dx - 22, dy);
      ionGrad.addColorStop(0, 'rgba(0, 240, 255, 0.85)');
      ionGrad.addColorStop(1, 'rgba(0, 240, 255, 0)');
      ctx.strokeStyle = ionGrad;
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(dx, dy);
      ctx.lineTo(dx - 22, dy);
      ctx.stroke();

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(dx - 4, dy - 2, 8, 4);
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(dx + 2, dy - 1, 3, 2);
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(dx - 5, dy - 1, 1.5, 2);
    }

    // ⚡ Rooftop Arc-Welding / Electrical Spark Flashes (every ~10 seconds at tower #3 roof)
    const sparkPeriod = 9.5;
    const sparkPhase = (t + 3.2) % sparkPeriod;
    if (sparkPhase < 0.65) {
      const spProg = sparkPhase / 0.65;
      const spFlicker = Math.sin(spProg * Math.PI * 8) > 0;
      const spX = 195;
      const spY = laneY - 58;

      if (spFlicker) {
        ctx.fillStyle = 'rgba(56, 189, 248, 0.85)';
        ctx.beginPath();
        ctx.arc(spX, spY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(spX - 1, spY - 1, 3, 3);

        for (let s = 0; s < 3; s++) {
          const sOffX = Math.sin(t * 20 + s) * 6;
          const sOffY = -Math.abs(Math.cos(t * 25 + s) * 6);
          ctx.fillStyle = s % 2 === 0 ? '#fef08a' : '#38bdf8';
          ctx.fillRect(spX + sOffX, spY + sOffY, 1.5, 1.5);
        }
      }
    }

    // 🔦 Subtle Sky Searchlight Sweep across the clouds (every ~20 seconds)
    {
      const lightAngle = Math.sin(t * 0.45) * 0.55;
      const beamOriginX = 125;
      const beamOriginY = laneY - 88;
      const beamLen = 140;
      const bTopX = beamOriginX + Math.sin(lightAngle) * beamLen;
      const bTopY = Math.max(visibleTop, beamOriginY - Math.cos(lightAngle) * beamLen);

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const sBeamGrad = ctx.createLinearGradient(beamOriginX, beamOriginY, bTopX, bTopY);
      sBeamGrad.addColorStop(0, 'rgba(56, 189, 248, 0.18)');
      sBeamGrad.addColorStop(0.6, 'rgba(186, 230, 253, 0.06)');
      sBeamGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = sBeamGrad;
      ctx.beginPath();
      ctx.moveTo(beamOriginX - 3, beamOriginY);
      ctx.lineTo(beamOriginX + 3, beamOriginY);
      ctx.lineTo(bTopX + 22, bTopY);
      ctx.lineTo(bTopX - 22, bTopY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // 🏭 Refinery Cooling Stack Warning Strobes & Billowing Vapor Plumes
    for (const st of REFINERY_STACKS) {
      const beaconOn = (Math.floor(t * 4 + st.x) % 2) === 0;
      ctx.fillStyle = beaconOn ? st.blinkColor : '#0f172a';
      ctx.fillRect(st.x + st.w / 2 - 2, laneY - st.h - 3, 4, 3);

      const puffProg = (t * 1.6 + st.x * 0.05) % 1.0;
      ctx.fillStyle = `rgba(148, 163, 184, ${0.35 * (1 - puffProg)})`;
      ctx.fillRect(st.x + st.w / 2 - 5 + Math.sin(t * 3 + st.x) * 4, laneY - st.h - 4 - puffProg * 24, 10 + puffProg * 8, 8 + puffProg * 6);
    }

    // Glowing Coolant Pulse racing through the refinery pipeline
    const pipePulseX = 510 + ((t * 110) % 260);
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(pipePulseX, laneY - 33, 20, 3);

    // 🛰️ Orbital Elevator Mag-Lift Cargo Gondola & Aircraft Warning Strobes
    const gondolaY = laneY - 36 - ((t * 16) % 68);
    ctx.fillStyle = '#facc15';
    ctx.fillRect(947, gondolaY, 14, 8);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(949, gondolaY + 2, 10, 2);

    const spireBlink = Math.sin(t * 5) > 0;
    ctx.fillStyle = spireBlink ? '#ef4444' : '#38bdf8';
    ctx.fillRect(952, laneY - 124, 4, 6);
    ctx.fillStyle = spireBlink ? '#ffffff' : '#00f0ff';
    ctx.fillRect(932, laneY - 78, 3, 3);
    ctx.fillRect(973, laneY - 78, 3, 3);

    // 🔮 Floating Void Crystal with Rotating Energy Ring (bob + spin)
    {
      const pylonX = 1440;
      const pylonY = laneY - 58 + Math.sin(t * 2.2) * 4;
      ctx.fillStyle = 'rgba(0, 240, 255, 0.3)';
      ctx.beginPath();
      ctx.arc(pylonX, pylonY, 14, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#00f0ff';
      ctx.beginPath();
      ctx.moveTo(pylonX, pylonY - 12);
      ctx.lineTo(pylonX + 8, pylonY);
      ctx.lineTo(pylonX, pylonY + 12);
      ctx.lineTo(pylonX - 8, pylonY);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(pylonX - 2, pylonY - 2, 4, 4);

      ctx.strokeStyle = 'rgba(56, 189, 248, 0.65)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(pylonX, pylonY, 16, 5, t * 2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 🧬 Hive Spire Pulsing Creep Veins
    {
      const creepPulse = 0.5 + 0.5 * Math.sin(t * 3.5);
      ctx.fillStyle = `rgba(192, 132, 252, ${0.45 * creepPulse})`;
      ctx.fillRect(1338, laneY - 76, 4, 60);
      ctx.fillRect(1332, laneY - 50, 16, 3);
      ctx.fillStyle = `rgba(244, 63, 94, ${0.6 * creepPulse})`;
      ctx.fillRect(1341, laneY - 86, 4, 4);
    }

    // ✈️🛰️🚂 Sporadic Background Traffic (satellite / airplane / far horizon train)
    {
      const satPeriod = 34, satDuration = 15;
      const satPhase = t % satPeriod;
      if (satPhase < satDuration) {
        const prog = satPhase / satDuration;
        const sx = visibleLeft - 30 + prog * (bgWidth + 60);
        const sy = visibleTop + 26 + Math.sin(prog * Math.PI) * 8;
        const satGlint = 0.5 + 0.5 * Math.sin(t * 9);
        ctx.fillStyle = 'rgba(100, 116, 139, 0.8)';
        ctx.fillRect(sx - 4, sy - 0.5, 3, 1.5);
        ctx.fillRect(sx + 2, sy - 0.5, 3, 1.5);
        ctx.fillStyle = `rgba(226, 232, 240, ${satGlint})`;
        ctx.fillRect(sx - 1, sy - 1, 2, 2);
      }

      const planePeriod = 23, planeDuration = 9;
      const planePhase = (t + 11) % planePeriod;
      if (planePhase < planeDuration) {
        const prog = planePhase / planeDuration;
        const px = visibleRight + 30 - prog * (bgWidth + 60);
        const py = visibleTop + 58 + Math.sin(prog * 5) * 3;
        ctx.fillStyle = 'rgba(203, 213, 225, 0.16)';
        ctx.fillRect(px + 5, py + 0.5, 30, 1.2);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(px - 6, py, 12, 1.8);
        const blink = Math.sin(t * 6) > 0.3;
        ctx.fillStyle = blink ? '#ef4444' : '#1e293b';
        ctx.fillRect(px - 6, py - 1, 1.8, 1.5);
        ctx.fillStyle = blink ? '#4ade80' : '#1e293b';
        ctx.fillRect(px + 4.2, py - 1, 1.8, 1.5);
      }

      const trainPeriod = 46, trainDuration = 17;
      const trainPhase = (t + 27) % trainPeriod;
      if (trainPhase < trainDuration) {
        const prog = trainPhase / trainDuration;
        const tx0 = visibleLeft - 60 + prog * (bgWidth + 120);
        const ty = laneY - 10;
        ctx.fillStyle = '#0a0e1a';
        ctx.fillRect(tx0, ty - 5, 46, 6);
        for (let w = 0; w < 4; w++) {
          const flick = 0.35 + 0.65 * Math.abs(Math.sin(t * 5 + w * 2));
          ctx.fillStyle = `rgba(253, 224, 71, ${flick})`;
          ctx.fillRect(tx0 + 4 + w * 11, ty - 3, 3, 2);
        }
        const smokeP = (t * 22 + tx0) % 10;
        ctx.fillStyle = `rgba(148, 163, 184, ${0.22 * (1 - smokeP / 10)})`;
        ctx.fillRect(tx0 + 42, ty - 8 - smokeP, 5, 5);
      }
    }

    // 🌕 Moonlight Plate Sheen & Pulsing Energy Data Packets racing along the floor conduits
    {
      const floorRowH2 = laneH / 10;
      const floorTopY2 = laneY + floorRowH2;
      const plateRows2 = 9;
      for (let i = 0; i <= plateRows2; i++) {
        const py = floorTopY2 + i * floorRowH2;
        if (i >= 1 && i <= 8) {
          const sheenDrift = Math.sin(t * 0.9 + i * 0.8) * 55;
          const sheenWidth = 320 + Math.sin(t * 1.4 + i) * 70;
          const cx = 700 + sheenDrift;
          ctx.save();
          ctx.translate(cx, py);
          ctx.scale(sheenWidth * 0.5, 1);
          ctx.globalAlpha = moonFlickerAccent;
          ctx.fillStyle = getMoonSheenGrad(ctx);
          ctx.fillRect(-1, -1, 2, 3.5);
          ctx.restore();
        }

        if (i % 2 === 1) {
          const pulseX = (t * 140 + i * 160) % (this.width + 120) - 60;
          ctx.fillStyle = i % 4 === 1 ? '#00f0ff' : '#ff0055';
          ctx.fillRect(pulseX, py, 32, 2);
        }
      }
    }

    // 🌧️ & 💧 Dynamic Rain Weather & Animated Specular Reflective Pools
    // Exactly 2 expansive, organic pools with undulating wavelets and real-time reflections
    {
      const weatherPeriod = 110;
      const rainStart = 20;
      const rainDuration = 22;
      const puddleDryDuration = 38;
      const cyclePos = t % weatherPeriod;

      let rainIntensity = 0;
      if (cyclePos >= rainStart && cyclePos < rainStart + rainDuration) {
        const rAge = cyclePos - rainStart;
        rainIntensity = Math.min(1.0, Math.min(rAge / 2.5, (rainDuration - rAge) / 2.5));
      }

      let puddleWetness = 0;
      if (cyclePos >= rainStart && cyclePos < rainStart + rainDuration + puddleDryDuration) {
        const pAge = cyclePos - rainStart;
        if (pAge <= rainDuration) {
          puddleWetness = Math.min(1.0, pAge / 3.5);
        } else {
          const dryAge = pAge - rainDuration;
          puddleWetness = Math.max(0.0, 1.0 - (dryAge / puddleDryDuration));
        }
      }

      const puddlePools = [
        { x: 470, y: laneY + 75, rx: 82, ry: 21, seed: 1.7 },
        { x: 1120, y: laneY + 95, rx: 90, ry: 23, seed: 4.3 }
      ];

      if (puddleWetness > 0.01) {
        for (const p of puddlePools) {
          const curRx = p.rx * (0.6 + 0.4 * puddleWetness);
          const curRy = p.ry * (0.6 + 0.4 * puddleWetness);
          const waveT = t * 3.8 + p.seed;

          ctx.save();

          const basinGrad = ctx.createRadialGradient(p.x, p.y, curRy * 0.3, p.x, p.y, curRx);
          basinGrad.addColorStop(0, `rgba(2, 6, 23, ${0.82 * puddleWetness})`);
          basinGrad.addColorStop(0.7, `rgba(10, 15, 30, ${0.68 * puddleWetness})`);
          basinGrad.addColorStop(1, 'rgba(15, 23, 42, 0)');
          ctx.fillStyle = basinGrad;
          ctx.beginPath();
          ctx.ellipse(p.x, p.y, curRx, curRy, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.globalCompositeOperation = 'screen';
          const waveCount = 5;
          for (let w = 0; w < waveCount; w++) {
            const sliceProg = (w / waveCount);
            const wy = p.y - curRy * 0.6 + sliceProg * (curRy * 1.2);
            const wWidth = Math.sqrt(Math.max(0, 1 - Math.pow((wy - p.y) / curRy, 2))) * curRx * 1.6;
            const wOsc = Math.sin(waveT * 1.5 + w * 1.8) * 1.6;
            const wAlpha = (0.08 + 0.06 * Math.sin(waveT * 2 + w)) * puddleWetness;

            ctx.strokeStyle = `rgba(186, 230, 253, ${wAlpha})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(p.x - wWidth / 2, wy + wOsc);
            ctx.bezierCurveTo(
              p.x - wWidth / 4, wy - wOsc,
              p.x + wWidth / 4, wy + wOsc,
              p.x + wWidth / 2, wy - wOsc
            );
            ctx.stroke();
          }

          const moonXTarget = p.x + (1220 - p.x) * 0.12;
          const moonSheenOsc = Math.sin(waveT * 2.2) * 4;
          const moonSheenW = curRx * (0.55 + 0.1 * Math.sin(waveT * 1.8));
          const moonSheenAlpha = (0.32 + 0.18 * Math.sin(waveT * 2.5)) * puddleWetness * moonFlickerAccent;

          const moonSheenGrad = ctx.createLinearGradient(moonXTarget - moonSheenW / 2, p.y, moonXTarget + moonSheenW / 2, p.y);
          moonSheenGrad.addColorStop(0, 'rgba(224, 242, 254, 0)');
          moonSheenGrad.addColorStop(0.5, `rgba(224, 242, 254, ${moonSheenAlpha})`);
          moonSheenGrad.addColorStop(1, 'rgba(224, 242, 254, 0)');
          ctx.fillStyle = moonSheenGrad;
          ctx.beginPath();
          ctx.ellipse(moonXTarget + moonSheenOsc, p.y, moonSheenW / 2, 3.5, 0, 0, Math.PI * 2);
          ctx.fill();

          if (this.particles?.lights?.length > 0) {
            for (const l of this.particles.lights) {
              const ldx = l.x - p.x;
              const ldy = l.y - p.y;
              const lDist = Math.hypot(ldx, ldy);
              if (lDist < 200 && l.alpha > 0.1) {
                const refX = p.x + ldx * 0.35 + Math.sin(waveT * 3 + l.x) * 3;
                const refW = Math.min(curRx * 0.65, 30 + (200 - lDist) * 0.25);
                const refAlpha = Math.min(0.75, (1.0 - lDist / 200) * l.alpha * puddleWetness * 1.2);

                const refGrad = ctx.createRadialGradient(refX, p.y, 2, refX, p.y, refW);
                refGrad.addColorStop(0, l.color);
                refGrad.addColorStop(0.6, l.color);
                refGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
                ctx.fillStyle = refGrad;
                ctx.globalAlpha = refAlpha;
                ctx.beginPath();
                ctx.ellipse(refX, p.y, refW, curRy * 0.65, 0, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }

          if (battlefield?.units?.length > 0) {
            for (const u of battlefield.units) {
              const udx = u.x - p.x;
              const udy = u.y - p.y;
              if (Math.abs(udx) < curRx * 1.1 && Math.abs(udy) < 45) {
                const uColor = u.team === 'western' ? 'rgba(0, 240, 255, 0.35)' : 'rgba(255, 0, 60, 0.35)';
                const uRefX = p.x + udx + Math.sin(waveT * 2 + u.x) * 2;
                ctx.fillStyle = uColor;
                ctx.globalAlpha = 0.45 * puddleWetness;
                ctx.beginPath();
                ctx.ellipse(uRefX, p.y + 2, 14, 4, 0, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }

          if (rainIntensity > 0.05) {
            for (let r = 0; r < 2; r++) {
              const ripProg = (t * 3.5 + p.seed * 2.5 + r * 0.5) % 1.0;
              const ripR = 4 + ripProg * (curRx * 0.55);
              const ripAlpha = (1.0 - ripProg) * 0.5 * rainIntensity;
              const ripX = p.x + (Math.sin(p.seed * 5 + r * 3) * (curRx * 0.35));
              const ripY = p.y + (Math.cos(p.seed * 4 + r * 2) * (curRy * 0.35));

              ctx.strokeStyle = `rgba(224, 242, 254, ${ripAlpha})`;
              ctx.lineWidth = 1.2;
              ctx.beginPath();
              ctx.ellipse(ripX, ripY, ripR, ripR * 0.35, 0, 0, Math.PI * 2);
              ctx.stroke();
            }
          }

          if (puddleWetness > 0.05 && rainIntensity < 0.25) {
            const dryingFactor = Math.min(1.0, puddleWetness * 1.8);
            for (let v = 0; v < 2; v++) {
              const vProg = (t * 0.85 + p.seed * 3.0 + v * 0.5) % 1.0;
              const vY = p.y - vProg * 18;
              const vX = p.x + Math.sin(t * 1.6 + v * 2.2 + p.seed) * (curRx * 0.4);
              const vAlpha = Math.sin(vProg * Math.PI) * 0.16 * dryingFactor;
              const vR = 6 + vProg * 10;
              ctx.fillStyle = `rgba(224, 242, 254, ${vAlpha})`;
              ctx.beginPath();
              ctx.ellipse(vX, vY, vR * 1.4, vR * 0.55, 0, 0, Math.PI * 2);
              ctx.fill();
            }
          }

          ctx.restore();
        }
      }
    }

    // ✨ Ambient Bioluminescent Energy Spores & Fireflies (30 drifting motes across lane)
    for (let i = 0; i < 30; i++) {
      const seed = i * 47.19;
      const fx = (seed * 13 + t * (14 + (i % 5) * 4)) % (this.width + 80) - 40;
      const fy = laneY + 15 + ((seed * 7 + Math.sin(t * 1.4 + i) * 30 + (i * 27)) % (laneH - 30));
      const pulse = (Math.sin(t * 3.5 + i * 1.7) + 1) * 0.5;
      const alpha = 0.25 + pulse * 0.65;
      const colors = ['#00f0ff', '#bef264', '#fde047', '#e879f9', '#38bdf8', '#4ade80', '#fb7185'];
      const c = colors[i % colors.length];
      ctx.fillStyle = c;
      ctx.globalAlpha = alpha;
      ctx.fillRect(fx, fy, i % 3 === 0 ? 3 : 2, i % 3 === 0 ? 3 : 2);
      if (i % 4 === 0) {
        ctx.fillStyle = c;
        ctx.globalAlpha = alpha * 0.35;
        ctx.fillRect(fx - 2, fy - 2, 7, 7);
      }
    }
    ctx.globalAlpha = 1.0;

    // 🔦 Sweeping Base Searchlight Cones
    {
      const wAngle = Math.sin(t * 0.7) * 0.35;
      ctx.save();
      ctx.translate(16, laneY + laneH / 2);
      ctx.rotate(wAngle);
      // Always the same local-space gradient (0,0,10 -> 280,0,280) - only the
      // save/translate/rotate above changes frame to frame, so bake it once
      // (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md §4, mirrors
      // the already-cached _topBeamGrad/_botBeamGrad pattern above).
      if (!this._westSearchlightGrad) {
        const g = ctx.createRadialGradient(0, 0, 10, 280, 0, 280);
        g.addColorStop(0, 'rgba(0, 240, 255, 0.16)');
        g.addColorStop(0.7, 'rgba(0, 240, 255, 0.04)');
        g.addColorStop(1, 'rgba(0, 240, 255, 0)');
        this._westSearchlightGrad = g;
      }
      ctx.fillStyle = this._westSearchlightGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(280, -65);
      ctx.lineTo(280, 65);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      const eAngle = Math.sin(t * 0.7 + 1.6) * 0.35;
      ctx.save();
      ctx.translate(this.width - 16, laneY + laneH / 2);
      ctx.rotate(Math.PI + eAngle);
      if (!this._eastSearchlightGrad) {
        const g = ctx.createRadialGradient(0, 0, 10, 280, 0, 280);
        g.addColorStop(0, 'rgba(255, 0, 60, 0.16)');
        g.addColorStop(0.7, 'rgba(255, 0, 60, 0.04)');
        g.addColorStop(1, 'rgba(0, 240, 255, 0)');
        this._eastSearchlightGrad = g;
      }
      ctx.fillStyle = this._eastSearchlightGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(280, -65);
      ctx.lineTo(280, 65);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // ♨️ Exhaust Vent Steam Puffs & Thermal Heat Mirage Shimmer
    {
      const ventPositions = [320, 1280];
      for (const vx of ventPositions) {
        const steamProg = (t * 1.5 + vx) % 1.0;
        ctx.fillStyle = `rgba(220, 240, 255, ${0.4 * (1 - steamProg)})`;
        ctx.fillRect(vx + 4 + Math.sin(t * 3) * 4, lowerY - steamProg * 28, 14, 10);

        const heatWave = Math.sin(t * 7 + vx) * 3.5;
        ctx.fillStyle = 'rgba(254, 215, 170, 0.12)';
        ctx.fillRect(vx + 2 + heatWave, lowerY - 4 - steamProg * 18, 20, 8);
      }
    }

    // 🏟️ Stadium Floodlight Tower Volumetric Beams, Lens Flares & Beacons
    if (this.quality !== 'low') {
      const floodPulse = 0.92 + 0.08 * Math.sin(t * 3.2);
      const ulMastX = 42, ulTopY = 48, ulHeadX = 64, ulHeadY = 52, ulTargetX = 160, ulTargetY = 320;
      const urMastX = 1558, urTopY = 48, urHeadX = 1536, urHeadY = 52, urTargetX = 1440, urTargetY = 320;

      if (!this._floodlightGrads) {
        const grads = { ulBeam: [], urBeam: [] };
        for (let pass = 0; pass < 3; pass++) {
          const passBase = pass === 0 ? 0.48 : (pass === 1 ? 0.24 : 0.10);
          const ul = ctx.createLinearGradient(ulHeadX, ulHeadY, ulTargetX + 80, ulTargetY + 60);
          ul.addColorStop(0, `rgba(255, 255, 255, ${passBase * 1.3})`);
          ul.addColorStop(0.15, `rgba(186, 230, 253, ${passBase * 1.0})`);
          ul.addColorStop(0.45, `rgba(56, 189, 248, ${passBase * 0.70})`);
          ul.addColorStop(0.80, `rgba(14, 165, 233, ${passBase * 0.25})`);
          ul.addColorStop(1, 'rgba(0, 0, 0, 0)');
          grads.ulBeam.push(ul);

          const ur = ctx.createLinearGradient(urHeadX, urHeadY, urTargetX - 80, urTargetY + 60);
          ur.addColorStop(0, `rgba(255, 255, 255, ${passBase * 1.3})`);
          ur.addColorStop(0.15, `rgba(254, 240, 138, ${passBase * 1.0})`);
          ur.addColorStop(0.45, `rgba(249, 115, 22, ${passBase * 0.70})`);
          ur.addColorStop(0.80, `rgba(239, 68, 68, ${passBase * 0.25})`);
          ur.addColorStop(1, 'rgba(0, 0, 0, 0)');
          grads.urBeam.push(ur);
        }

        const ulCore = ctx.createLinearGradient(ulHeadX, ulHeadY, ulTargetX + 30, ulTargetY + 30);
        ulCore.addColorStop(0, 'rgba(255, 255, 255, 0.55)');
        ulCore.addColorStop(0.20, 'rgba(186, 230, 253, 0.40)');
        ulCore.addColorStop(0.60, 'rgba(56, 189, 248, 0.20)');
        ulCore.addColorStop(1, 'rgba(0, 0, 0, 0)');
        grads.ulCore = ulCore;

        const ulPool = ctx.createRadialGradient(ulTargetX, ulTargetY, 15, ulTargetX, ulTargetY, 160);
        ulPool.addColorStop(0, 'rgba(255, 255, 255, 0.40)');
        ulPool.addColorStop(0.25, 'rgba(186, 230, 253, 0.28)');
        ulPool.addColorStop(0.60, 'rgba(56, 189, 248, 0.14)');
        ulPool.addColorStop(0.85, 'rgba(14, 165, 233, 0.04)');
        ulPool.addColorStop(1, 'rgba(0, 0, 0, 0)');
        grads.ulPool = ulPool;

        const urCore = ctx.createLinearGradient(urHeadX, urHeadY, urTargetX - 30, urTargetY + 30);
        urCore.addColorStop(0, 'rgba(255, 255, 255, 0.55)');
        urCore.addColorStop(0.20, 'rgba(254, 240, 138, 0.40)');
        urCore.addColorStop(0.60, 'rgba(249, 115, 22, 0.20)');
        urCore.addColorStop(1, 'rgba(0, 0, 0, 0)');
        grads.urCore = urCore;

        const urPool = ctx.createRadialGradient(urTargetX, urTargetY, 15, urTargetX, urTargetY, 160);
        urPool.addColorStop(0, 'rgba(255, 255, 255, 0.40)');
        urPool.addColorStop(0.25, 'rgba(254, 240, 138, 0.28)');
        urPool.addColorStop(0.60, 'rgba(249, 115, 22, 0.14)');
        urPool.addColorStop(0.85, 'rgba(239, 68, 68, 0.04)');
        urPool.addColorStop(1, 'rgba(0, 0, 0, 0)');
        grads.urPool = urPool;

        this._floodlightGrads = grads;
      }

      // Upper-left volumetric light shaft -> Western Base
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = floodPulse;
      for (let pass = 0; pass < 3; pass++) {
        const spreadMul = 1.0 + pass * 0.35;
        ctx.fillStyle = this._floodlightGrads.ulBeam[pass];

        ctx.beginPath();
        ctx.moveTo(ulHeadX, ulHeadY);
        ctx.lineTo(ulTargetX + 110 * spreadMul, ulTargetY - 80 * spreadMul);
        ctx.quadraticCurveTo(ulTargetX + 140 * spreadMul, ulTargetY, ulTargetX + 90 * spreadMul, ulTargetY + 90 * spreadMul);
        ctx.closePath();
        ctx.fill();
      }

      ctx.fillStyle = this._floodlightGrads.ulCore;
      ctx.beginPath();
      ctx.moveTo(ulHeadX, ulHeadY);
      ctx.lineTo(ulTargetX + 50, ulTargetY - 30);
      ctx.lineTo(ulTargetX + 20, ulTargetY + 40);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = this._floodlightGrads.ulPool;
      ctx.beginPath();
      ctx.ellipse(ulTargetX, ulTargetY + 10, 160, 72, 0.15, 0, Math.PI * 2);
      ctx.fill();

      for (let m = 0; m < 5; m++) {
        const mProg = ((t * 15 + m * 38) % 180);
        const mx = ulHeadX + mProg * 0.75;
        const my = ulHeadY + mProg * 1.35 + Math.sin(t * 2 + m) * 6;
        ctx.fillStyle = `rgba(224, 242, 254, ${0.35 + 0.25 * Math.sin(t * 3 + m)})`;
        ctx.fillRect(mx, my, 1.5, 1.5);
      }
      ctx.restore();

      // Upper-left floodlight head flare (re-establishes the head's own transform)
      ctx.save();
      ctx.translate(ulHeadX, ulHeadY);
      ctx.rotate(0.78);
      ctx.fillStyle = `rgba(56, 189, 248, ${0.48 * floodPulse})`;
      ctx.beginPath();
      ctx.arc(0, 0, 19, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 255, ${0.75 * floodPulse})`;
      ctx.beginPath();
      ctx.arc(0, 0, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(186, 230, 253, ${0.60 * floodPulse})`;
      ctx.fillRect(-24, -1.0, 48, 2.0);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.80 * floodPulse})`;
      ctx.fillRect(-12, -0.5, 24, 1.0);
      ctx.restore();

      // Upper-left tower beacon
      const ulBeacon = Math.sin(t * 4.5) > 0.1;
      ctx.fillStyle = ulBeacon ? '#ef4444' : '#1e293b';
      ctx.fillRect(ulMastX - 2, ulTopY - 14, 4, 5);
      if (ulBeacon) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.55)';
        ctx.beginPath();
        ctx.arc(ulMastX, ulTopY - 12, 7, 0, Math.PI * 2);
        ctx.fill();
      }

      // Upper-right volumetric light shaft -> Eastern Base
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = floodPulse;
      for (let pass = 0; pass < 3; pass++) {
        const spreadMul = 1.0 + pass * 0.35;
        ctx.fillStyle = this._floodlightGrads.urBeam[pass];

        ctx.beginPath();
        ctx.moveTo(urHeadX, urHeadY);
        ctx.lineTo(urTargetX - 110 * spreadMul, urTargetY - 80 * spreadMul);
        ctx.quadraticCurveTo(urTargetX - 140 * spreadMul, urTargetY, urTargetX - 90 * spreadMul, urTargetY + 90 * spreadMul);
        ctx.closePath();
        ctx.fill();
      }

      ctx.fillStyle = this._floodlightGrads.urCore;
      ctx.beginPath();
      ctx.moveTo(urHeadX, urHeadY);
      ctx.lineTo(urTargetX - 50, urTargetY - 30);
      ctx.lineTo(urTargetX - 20, urTargetY + 40);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = this._floodlightGrads.urPool;
      ctx.beginPath();
      ctx.ellipse(urTargetX, urTargetY + 10, 160, 72, -0.15, 0, Math.PI * 2);
      ctx.fill();

      for (let m = 0; m < 5; m++) {
        const mProg = ((t * 15 + m * 38) % 180);
        const mx = urHeadX - mProg * 0.75;
        const my = urHeadY + mProg * 1.35 + Math.sin(t * 2 + m) * 6;
        ctx.fillStyle = `rgba(254, 240, 138, ${0.35 + 0.25 * Math.sin(t * 3 + m)})`;
        ctx.fillRect(mx, my, 1.5, 1.5);
      }
      ctx.restore();

      // Upper-right floodlight head flare
      ctx.save();
      ctx.translate(urHeadX, urHeadY);
      ctx.rotate(-0.78);
      ctx.fillStyle = `rgba(254, 240, 138, ${0.48 * floodPulse})`;
      ctx.beginPath();
      ctx.arc(0, 0, 19, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 255, ${0.75 * floodPulse})`;
      ctx.beginPath();
      ctx.arc(0, 0, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(254, 240, 138, ${0.60 * floodPulse})`;
      ctx.fillRect(-24, -1.0, 48, 2.0);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.80 * floodPulse})`;
      ctx.fillRect(-12, -0.5, 24, 1.0);
      ctx.restore();

      // Upper-right tower beacon
      const urBeacon = Math.sin(t * 4.5 + 1.5) > 0.1;
      ctx.fillStyle = urBeacon ? '#ef4444' : '#1e293b';
      ctx.fillRect(urMastX - 2, urTopY - 14, 4, 5);
      if (urBeacon) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.55)';
        ctx.beginPath();
        ctx.arc(urMastX, urTopY - 12, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  },

  renderBackground(ctx, battlefield) {
    this.renderStaticBackground(ctx, battlefield);
    this.renderDynamicBackground(ctx, battlefield);
  }
};


